<template>
  <div class="location-view" :class="{ 'location-view--signed': isActingSigned }">
    <!-- One establishment screen for everyone in the window, founders
         included: the sides, who leads them, who is on them, and the clock.
         Whether it also *offers* a side is `may_choose_side`, the same flag
         the server gates `choose_side`/`create_side` on — so nobody is shown
         a choice the server would refuse. `hasPendingFight` already waits on
         the clock that carries the sides, so there is no data-less moment for
         a second screen to cover. -->
    <div v-if="hasPendingFight" class="side-donut-overlay">
      <FightEstablishmentStage
          ref="sideDonut"
          :sides="fightClock?.sides || []"
          :participants="fightClock?.participants || []"
          :selectable="hasSideChoice"
          :self-character-id="playerGeneralInfo?.id || null"
          :location-name="fightLocationName"
          :round="fightRound"
          :deadline="fightClock?.phase_ends_at || null"
          :skew-ms="fightSkewMs"
          :background-url="fightBackgroundUrl"
          :locked="fightResolving"
          :duel="!!fightClock?.duel"
          :training="!!fightClock?.training"
          :sealed="!!fightClock?.sealed"
          @select-side="handleChooseSide"
          @create-side="handleCreateSide"
          @expired="handleEstablishmentExpired"
      />
    </div>
    <FightOverlay :side="playerGeneralInfo?.path?.name" v-if="isInFight"/>
    <FightChallengeOverlay
        v-if="pendingChallenge && !hasPendingFight && !isInFight"
        ref="challengeOverlay"
        :key="pendingChallenge.id"
        :challenge="pendingChallenge"
        :background-url="duelBackgroundUrl"
        @accept="handleChallengeAccept"
        @reject="handleChallengeReject"
        @dismiss="handleChallengeDismiss"
    />

    <!-- The fight's own clock. It lives here rather than inside the board
         because a regular fight is not always tactical, and because the board
         must keep rendering through the ordinary `screen === 'tactical'` path. -->
    <div v-if="showFightRail" ref="fightRail" class="fight-rail-slot">
      <FightStatusRail
          :state="fightClock"
          :skew-ms="fightSkewMs"
          :self-character-id="playerGeneralInfo?.id || null"
          :hide-roster="inTacticalMode"
          @surrender="openSurrenderConfirm"
          @end-vote="openEndVoteConfirm"
          @end-training="openEndTrainingConfirm"
      />
    </div>

    <!-- Challenging is an ordinary character action against whoever is selected,
         so the one control serves the world view and the tactical board alike. -->
    <div v-if="canChallengeSelected" class="challenge-slot">
      <HudButton :type="ButtonType.SUBMIT" :disabled="issuingChallenge" @click="issueChallenge">
        {{ $t('fightHud.challenge') }}
      </HudButton>
    </div>

    <!-- The bar sits at z-index 2000, above every overlay, so it has to be gone
         while a modal is asking for an answer — not merely behind it. -->
    <div class="rpg-action-holder"
         :class="{ 'rpg-action-holder--stowed': isMoving }"
         v-if="!hasPendingFight && !pendingChallenge"
    >
      <ActionHolder
          :items="availableItems"
          :skills="availableActions"
          :specials="playerSpecials"
          :player-service="playerService"
          :current-phase="effectivePhase"
          :safe-zone="inSafeZone"
          @skill-selected="handleSkillSelected"
          @item-selected="handleItemSelected"
          @special-selected="handleSpecialSelected"
          @action-hovered="handleActionHovered"
          @action-hover-end="handleActionHoverEnd"
          @offence-refused="refuseOffenceHere"
      />
      <ActionTriggerGroup
          class="trigger"
          :selected-action="selectedAction"
          :preview-action="hoveredAction"
          :tactical-movement-phase="isTacticalMovementPhase"
          :in-tactical-preparation="inTacticalPreparationButDisabled"
          :charges-placed="chargesPlaced"
          :charge-count="chargeCount"
          :waiting="isWaitingForOpponents"
          :in-fight="isInFight"
          @next-turn-triggered="handleActionTriggerClick"
          @safe-place-triggered="() => {}"
          @roll-dice-triggered="toogleDice"
          @backpack-triggered="toggleInventory"
          @end-tactical-move="handleEndTacticalMove"
      />
    </div>
    <!-- Background View or Tactical Map -->
    <!-- Priority: character.screen === 'tactical' > position.tactical_map exists -->
    <PlayerTacticalMap
        v-if="playerGeneralInfo?.screen === 'tactical'"
        ref="tacticalMap"
        :key="`${playerGeneralInfo?.id}:${activeTacticalMapId}`"
        :tactical-map-id="activeTacticalMapId"
        :player-character="playerGeneralInfo"
        :player-skills="availableActions"
        :player-items="availableItems"
        :player-specials="playerSpecials"
        :current-phase="effectivePhase"
        :tactical-movement-disabled="tacticalMovementDisabled"
        :in-fight="isInFight"
        :fight-id="fightClock?.fight_id || null"
        :fight-state="fightClock"
        :safe-top="fightSafeTop"
        class="background-view"
        @cell-clicked="handleTacticalCellClick"
        @object-selected="handleTacticalObjectSelected"
        @chest-clicked="handleChestClicked"
        @item-clicked="handleItemPickedUp"
        @interact-after-move="handleInteractAfterMove"
        @ability-cast="handleAbilityCast"
        @cell-hovered="handleCellHovered"
        @charges-updated="handleChargesUpdated"
    />
    <BackgroundView
        v-else
        :key="position?.id"
        :background="position?.image"
        :movementActivated="isMoving"
        :position-id="position?.id"
        class="background-view"
    ></BackgroundView>

    <!-- The fight clock, read as a dial in the middle of the screen rather
         than a number on the rail at the edge. It takes the character-card
         band's place during a fight: a fighter watching the seconds run out is
         not picking targets off the card strip. -->
    <div v-if="showFightRail" class="fight-dial-slot">
      <CountDown
          v-if="fightClock?.phase_ends_at"
          :deadline="fightClock.phase_ends_at"
          :started-at="fightClock.phase_started_at || null"
          :skew-ms="fightSkewMs"
          :resolving="fightResolving"
          @expired="handleFightPhaseExpired"
      />
    </div>

    <!-- Top Row (Character Cards). The tactical board already shows every
         character as a token on the grid, so the card band is redundant there
         and only steals the top of the screen. -->
    <div v-if="!inTacticalMode"
         class="top-row" :class="{ 'top-row--stowed': isMoving, 'top-row--behind-dial': showFightRail }">
      <NewCharacterCardHolder
          v-if="getCharacters().length > 0"
          :class="{ 'no-target-feedback': showNoTargetFeedback }"
          :characters="getCharacters()"
          :selectedCharacterId="selectedGameObjectId"
          :additionalCharactersData="additionalCharactersData"
          :highlightedCharacterIds="highlightedCharacterIds"
          :disableInteraction="false"
          @characterSelected="handleCharacterSelected"
          @characterHovered="handleCardHovered"
          @characterHoverLeave="handleCardHoverLeave"
      />
    </div>

    <!-- Compass Component (Centered) -->
    <div class="center-section">
      <div class="center-left">
        <CharacterRPGBars
            :character="{
          name: playerInfo?.name || 'Unknown',
          level: playerInfo?.rank_grade || 0,
          health: playerInfo?.attributes.find(attr => attr.name === 'Health')?.current || 0,
          maxHealth: playerInfo?.attributes.find(attr => attr.name === 'Health')?.max || 100,
          flow: playerInfo?.attributes.find(attr => attr.name === 'Energy')?.current || 0,
          maxFlow: playerInfo?.attributes.find(attr => attr.name === 'Energy')?.max || 100,
          actionPoints: playerInfo?.attributes.find(attr => attr.name === 'Action Points')?.current || 0,
          maxActionPoints: playerInfo?.attributes.find(attr => attr.name === 'Action Points')?.max || 10,
          avatar: playerGeneralInfo?.biography?.avatar || '',
          game_master_mode: playerGeneralInfo?.game_master_mode || false,
        }"
            :shields="shields"
            :effects="activeEffects"
            :pending-cost="pendingCost"
            :resetBaseStats="playerGeneralInfo?.resetting_base_stats || false"
            @openInfo="openInfo"
        ></CharacterRPGBars>
      </div>
      <div class="center-center">
        <CompassRPG
            v-if="isCompassVisible"
            :position="position"
            @move="handleMove"
        />
      </div>
      <div class="center-right">
        <div class="codex-rail">
          <MapColumn :map-data="mapData" :coordinates="playerInfo?.coordinates" class="mini-map"></MapColumn>

          <CodexHeading :label="$t('codexRail.location')" tone="gold" spine="head"/>
          <div class="codex-note">
            <CodexSpine variant="line"/>
            <p class="codex-note-text">{{ positionSummary }}</p>
          </div>

          <RPGActionLog
            :actions="actionLog"
            :current-cycle-number="currentCycleNumber"
            :current-phase="effectivePhase"
            :self-character-id="playerInfo?.id || null"
            class="action-log"
            @character-hover="handleActionLogCharacterHover"
            @character-hover-leave="handleActionLogCharacterLeave"
            @character-selected="handleActionLogCharacterSelected"
          ></RPGActionLog>
        </div>
      </div>
      <DimensionalGlitch v-for="an in position?.anomalies || []" :key="an" :force-visible="false"
                         :glitch-id="an" @glitch-found="handleAnomalyClick"/>
    </div>
    <!-- Bottom Row -->

    <!-- GM Character Switch Button. Switching who the client plays says nothing
         about a training, which acts as its clone per request — offering it here
         would only promise something the route cannot do. -->
    <GMSwitchButton v-if="!isTrainingRoute" @openModal="gmModalOpen = true" />

    <!-- Signing the character being played is a game master power, so the seal
         sits with the other master-only controls rather than in the HUD. -->
    <div v-if="isGameMaster && !isTrainingRoute" class="gm-sign-dock">
      <FlowSignToggle
        :characterId="playerGeneralInfo?.id"
        :signed="isActingSigned"
        @changed="handleActingSignChanged"
      />
    </div>

    <!-- GM Character Selector Modal -->
    <GMCharacterSelectorModal
      :isOpen="gmModalOpen"
      :currentPositionId="position?.id || playerGeneralInfo?.position_id"
      @close="gmModalOpen = false"
      @characterSelected="handleGMCharacterSwitch"
    />

    <!-- Dice Roller Modal - Must be at root level to maintain proper z-index stacking -->
    <DiceRollerModal
      :visible="isDiceVisible"
      :challenge="currentChallenge"
      :death-save="deathSaveProgress"
      :death-save-failures-to-coma="deathSaveFailuresToComa"
      :death-save-successes-to-recover="deathSaveSuccessesToRecover"
      @close="toogleDice"
      @roll-complete="onDiceRollComplete"
    />

    <!-- Bargain Component - Modal overlay at root level -->
    <BargainComponent
      v-if="isBargainVisible"
      :inventory="inventoryItems || []"
      :bargain-id="currentBargainId"
      :character-id="playerInfo?.id || null"
      @bargain-completed="toggleBargain"
    />

    <!-- Long rest - Modal overlay at root level -->
    <LongRestComponent
      v-if="longRestVisible"
      :inventory="inventoryItems || []"
      :offer-id="currentLongRestOfferId"
      :position="position"
      @rest-completed="closeLongRest"
    />

    <!-- Chest loot - Modal overlay at root level -->
    <ChestComponent
      v-if="openChestId"
      :key="openChestId"
      :chest-id="openChestId"
      :chest-name="openChestName"
      @closed="closeChest"
      @looted="handleChestLooted"
    />

    <!-- Item Holder (Inventory) - Modal overlay at root level -->
    <ItemHolder
      v-if="isInventoryVisible"
      :items="inventoryItems || []"
      :row-count="5"
      :col-count="8"
      controls
      @item-clicked="onInventoryItemClicked"
      @close="toggleInventory"
    >
      <template #header>
        <span>{{ $t('player.inventory.title') }}</span>
      </template>
    </ItemHolder>

    <!-- RPG Player Profile - Modal overlay at root level -->
    <RPGPlayerProfile
      :is-open="playerProfileVisible"
      :effects="activeEffects"
      @close="closePlayerProfile"
    />

    <!-- Surrender confirmation. A component, never window.confirm() — a
         native dialog freezes the renderer and the fight clock keeps running
         behind it. The message spells out what surrendering actually does:
         coma, stay on the board, robbable — never a bare "are you sure?". -->
    <ConfirmModal
      :is-open="surrenderConfirmOpen"
      :title="$t('fightHud.surrenderConfirmTitle')"
      :message="$t('fightHud.surrenderConfirmMessage')"
      :confirm-label="$t('fightHud.surrenderConfirmYes')"
      :cancel-label="$t('fightHud.surrenderConfirmNo')"
      @confirm="confirmSurrender"
      @cancel="cancelSurrender"
    />

    <!-- Ending early is unanimous among the standing, so the confirmation says
         what the click actually is: a vote, not the end of the fight. -->
    <ConfirmModal
      :is-open="endVoteConfirmOpen"
      :title="$t(pendingEndVoteAgree ? 'fightHud.endVoteConfirmTitle' : 'fightHud.endVoteWithdrawTitle')"
      :message="$t(pendingEndVoteAgree ? 'fightHud.endVoteConfirmMessage' : 'fightHud.endVoteWithdrawMessage')"
      :confirm-label="$t(pendingEndVoteAgree ? 'fightHud.endVoteConfirmYes' : 'fightHud.endVoteWithdraw')"
      :cancel-label="$t('fightHud.endVoteConfirmNo')"
      @confirm="confirmEndVote"
      @cancel="cancelEndVote"
    />

    <!-- Ending a training deletes the clone and the dummy outright — there is
         no one else to vote with, so this replaces the end-vote control for
         the duration of a training fight. -->
    <ConfirmModal
      :is-open="endTrainingConfirmOpen"
      :title="$t('fightHud.endTrainingConfirmTitle')"
      :message="$t('fightHud.endTrainingConfirmMessage')"
      :confirm-label="$t('fightHud.endTrainingConfirmYes')"
      :cancel-label="$t('fightHud.endTrainingConfirmNo')"
      @confirm="confirmEndTraining"
      @cancel="cancelEndTraining"
    />

    <ConfirmModal
      :is-open="!!endTrainingErrorMessage"
      mode="alert"
      :title="$t('fightHud.endTrainingErrorTitle')"
      :message="endTrainingErrorMessage || ''"
      :confirm-label="$t('common.close')"
      @confirm="endTrainingErrorMessage = null"
    />

    <!-- Why an action did not happen. Every refusal the server issues carries a
         sentence written for a player; showing it is the difference between a
         rule and a dead button. A safe place refuses violence before the click
         is even sent, and that refusal arrives here too. -->
    <ConfirmModal
      :is-open="!!actionRefusalMessage"
      mode="alert"
      :title="actionRefusalTitle"
      :message="actionRefusalMessage || ''"
      :confirm-label="$t('common.close')"
      @confirm="actionRefusalMessage = null"
    />
  </div>
  <div class="bottom">
    <!-- Current Turn Component (Bottom, Left) -->
    <div class="left-action-group">
      <!-- The world clock's levers, for a GM only, hung off the counter they
           act on. Here rather than in the GM dock because the dock only appears
           on /game-master routes, and the reason to step the clock by hand is
           almost always something you are watching from inside the game. -->
      <CurrentTurnComponent
          class="current-turn-component"
          :current-turn="currentCycleNumber"
          :current-phase="effectivePhase"
      >
        <template #controls>
          <GameMasterCycleControl
              :fight-id="fightClock?.fight_id || null"
              @advanced="handleCycleAdvanced"
          />
        </template>
      </CurrentTurnComponent>
    </div>
    <!-- Dice Component (Bottom, Right) -->
  </div>
</template>


<script>
import CompassComponent from '@/components/Game/Location/Compass.vue';
import {
  ActionGameApi,
  CharacterGameApi,
  EffectsGameApi,
  ItemsGameApi,
  ShieldsGameApi,
  SkillsGameApi,
  WorldGameApi,
  DiceGameApi
} from "@/api/backendService.js";
import CharacterCardHolder from "@/components/Game/Location/CharacterCardHolder.vue";
import PlayerComponent from "@/components/Game/Location/PlayerComponent.vue";
import BackgroundView from "@/components/Game/Location/BackgroundView.vue";
import PlayerTacticalMap from "@/components/Player/PlayerTacticalMap.vue";
import ActionService from "@/services/actionsService.js";
import {holdsPendingAim} from "@/services/impact/pending";
import {isSelfTargetAbility} from "@/utils/selfTarget";
import abilityAvailability from "@/services/abilityAvailabilityService.js";
import PlayerService from "@/services/playerService";
import DiceComponent from "@/components/Dice/DiceComponent.vue";
import CurrentTurnComponent from "@/components/Game/CurrentTurnComponent.vue";
import CountDown from "@/components/Game/Fight/CountDown.vue";
import GameObjectSelector from "@/components/Selectors/GameObjectSelector.vue";
import SkillSelector from "@/components/Selectors/SkillSelector.vue";
import ActionPreview from "@/components/Pickers/ActionPreview.vue";
import CompactPlayButton from "@/components/btn/CompactPlayButton.vue";
import DiceRollerModal from "@/components/DiceRoller/DiceRollerModal.vue";
import CoordinatesDisplay from "@/components/Map/Coordinates.vue";
import EffectItem from "@/components/Effect/EffectItem.vue";
import EffectsHolder from "@/components/Effect/EffectsHolder.vue";
import {CharacterInfoGameService} from "@/services/characterInfoService.js";
import ItemCell from "@/components/Item/ItemCell.vue";
import ItemHolder from "@/components/Item/ItemHolder.vue";
import InventoryButton from "@/components/Action/InventoryButton.vue";
import ShieldHolder from "@/components/Shield/ShieldHolder.vue";
import MiniMapComponent from "@/components/Map/MiniMapComponent.vue";
import UserActionLogItem from "@/components/ActionLog/UserActionLogItem.vue";
import UserActionLog from "@/components/ActionLog/UserActionLog.vue";
import BargainComponent from "@/components/bargain/BargainComponent.vue";
import LongRestComponent from "@/components/LongRest/LongRestComponent.vue";
import ChestComponent from "@/components/chest/ChestComponent.vue";
import bargainService from "@/services/bargainService.js";
import {ensureConnection, reconnectConnection} from "@/api/dx-websocket/index.ts";
import DimensionalGlitch from "@/components/Glitch/DimensionalGlitch.vue";
import FightChallengeOverlay from "@/components/Fight/FightChallengeOverlay.vue";
import FightStatusRail from "@/components/Fight/FightStatusRail.vue";
import GameMasterCycleControl from "@/components/GameMaster/GameMasterCycleControl.vue";
import ConfirmModal from "@/components/Modal/ConfirmModal.vue";
import HudButton from "@/components/btn/HudButton.vue";
import {ButtonType} from "@/types/ButtonType";
import CharacterRPGBars from "@/components/PlayerRPGBars/CharacterRPGBars.vue";
import NewCharacterCardHolder from "@/components/Game/Location/NewCharacterCardHolder.vue";
import ActionHolder from "@/components/ActionArea/ActionHolder/ActionHolder.vue";
import ActionItem from "@/components/ActionArea/ActionItem/ActionItem.vue";
import ActionTrigger from "@/components/ActionArea/ActionTrigger/ActionTrigger.vue";
import ActionTriggerGroup from "@/components/ActionArea/ActionTriggerGroup/ActionTriggerGroup.vue";
import CompassRPG from "@/components/Compass/CompassRPG.vue";
import MapColumn from "@/components/MapColumn/MapColumn.vue";
import CodexHeading from "@/components/CodexRail/CodexHeading.vue";
import CodexSpine from "@/components/CodexRail/CodexSpine.vue";
import RPGActionLog from "@/components/RPGActionLog/RPGActionLog.vue";
import FightEstablishmentStage from "@/components/Fight/FightEstablishmentStage.vue";
import {FightService} from "@/services/PlayerFight";
import FightOverlay from "@/components/Fight/FightOverlay.vue";
import CharacterCurrentLocationService from "@/services/CharacterCurrentLocationService";
import RPGPlayerProfile from "@/components/Player/RPGPlayerProfile.vue";
import GMSwitchButton from "@/components/GameMaster/GMCharacterSwitch/GMSwitchButton.vue";
import FlowSignToggle from "@/components/GameMaster/FlowSignToggle.vue";
import GMCharacterSelectorModal from "@/components/GameMaster/GMCharacterSwitch/GMCharacterSelectorModal.vue";
import GMCharacterSwitchService from "@/services/GMCharacterSwitchService";
import {endTraining} from "@/services/trainingService";
import {ref} from "vue";
import {useLocationBackground} from "@/composables/useLocationBackground";

const CYCLE_RECONCILE_MS = 10000;
const MOVEMENT_ACTION_TYPES = ['MOVE', 'CHANGE_POSITION'];

export default {
  name: 'LocationView',
  components: {
    FightOverlay,
    FightEstablishmentStage,
    FightChallengeOverlay,
    FightStatusRail,
    GameMasterCycleControl,
    ConfirmModal,
    HudButton,
    RPGActionLog,
    MapColumn,
    CodexHeading,
    CodexSpine,
    CompassRPG,
    DiceRollerModal,
    ActionTriggerGroup,
    ActionTrigger,
    ActionItem,
    ActionHolder,
    CharacterRPGBars,
    NewCharacterCardHolder,
    DimensionalGlitch,
    BargainComponent,
    LongRestComponent,
    ChestComponent,
    UserActionLog,
    UserActionLogItem,
    MiniMapComponent,
    ShieldHolder,
    InventoryButton,
    ItemHolder,
    ItemCell,
    EffectsHolder,
    EffectItem,
    CoordinatesDisplay,
    CompactPlayButton,
    ActionPicker: ActionPreview,
    ActionSelector: SkillSelector,
    GameObjectSelector,
    CurrentTurnComponent,
    CountDown,
    DiceComponent,
    BackgroundView,
    PlayerTacticalMap,
    PlayerComponent,
    CharacterCardHolder,
    CompassComponent,
    RPGPlayerProfile,
    GMSwitchButton,
    FlowSignToggle,
    GMCharacterSelectorModal
  },
  setup() {
    /* `position` lives here rather than in data() so the background composables
       can watch it. Options API unwraps a returned ref, so the existing
       `this.position = …` assignments write straight through to it. */
    const position = ref(null);

    /* Lazy glob, never eager — see the composable. The bank is allowed to be
       empty, in which case this stays '' and the overlay keeps its painted
       texture. */
    const {backgroundUrl: fightBackgroundUrl} = useLocationBackground('fight', position);
    const {backgroundUrl: duelBackgroundUrl} = useLocationBackground('duel', position);
    return {ButtonType, position, fightBackgroundUrl, duelBackgroundUrl};
  },
  data() {
    return {
      bus: undefined,
      selectedGameObjectId: null,
      selectedAction: null,
      hoveredAction: null,
      connections: null,
      playerInfo: null,
      playerGeneralInfo: null,
      characters: [],
      npcCharacters: [],
      actionService: new ActionService(ActionGameApi),
      playerService: null,
      playerSkills: null,
      playerSpecials: [],
      diceResult: null,
      inventoryItems: null,
      longRestVisible: false,
      currentLongRestOfferId: null,
      activeEffects: [],
      shields: [],
      additionalCharactersData: null,
      diceVisible: false,
      bargainVisible: false,
      // The chest whose contents are on screen, and its name for the panel
      // title before the manifest arrives.
      openChestId: null,
      openChestName: 'Chest',
      inventoryVisible: false,
      playerProfileVisible: false,
      isMoving: false,
      isExecutingAction: false,
      mapData: {},
      actionLog: [],
      /* Whether `actionLog` is what the server last said, or the last window that
         survived a failed fetch. Absence from an untrusted log proves nothing. */
      actionLogTrusted: false,
      bargains: null,
      selectedBargainId: null,
      currentCycleNumber: null,
      currentCyclePhase: null,
      cycleSyncTimer: null,
      tacticalMovementDisabled: false,
      fightService: new FightService(),
      /* The fight's own clock, as the server last stated it. Null outside a
         fight, which is also what keeps the whole rail off the world view. */
      fightClock: null,
      fightSkewMs: 0,
      fightSafeTop: 0,
      fightSidesRefreshTimer: null,
      /* Retry state for `checkFightStatus`. See `scheduleFightStatusRetry`. */
      fightStatusRetryTimer: null,
      fightStatusRetries: 0,
      endingFightTurn: false,
      surrenderConfirmOpen: false,
      endVoteConfirmOpen: false,
      pendingEndVoteAgree: true,
      castingEndVote: false,
      endTrainingConfirmOpen: false,
      endingTraining: false,
      endTrainingErrorMessage: null,
      /* Why the last action did not happen, in the words the server used.
         Null while nothing has been refused. */
      actionRefusalMessage: null,
      actionRefusalCode: null,
      pendingChallenges: [],
      issuingChallenge: false,
      isInFight: false,
      hasPendingFight: false,
      fightLocationName: null,
      fightRound: null,
      currentChallenge: null,
      showNoTargetFeedback: false,
      highlightedCharacterIds: [], // Characters to highlight in tactical mode
      gmModalOpen: false, // GM character selector modal visibility
      hoverCardVisible: false, // Character hover card visibility
      hoverCardCharId: null, // Character ID for hover card
      hoverCardPosition: { x: 0, y: 0 }, // Position for hover card
      chargesPlaced: 0, // Number of charges placed so far
      chargeCount: 1, // Total charge count for current ability
    };
  },
  async mounted() {
    this.bus = ensureConnection();
    this.subscribeBus();
    this.subscribeFightClock();
    await this.loadPendingChallenges();
    window.addEventListener('resize', this.measureFightRail);
    const currentCycle = (await ActionGameApi.actionCurrentCycleRetrieve()).data;
    this.currentCycleNumber = currentCycle.number;
    this.currentCyclePhase = currentCycle.phase;

    // Add global event listeners for action cancellation
    window.addEventListener('keydown', this.handleGlobalKeydown);
    window.addEventListener('contextmenu', this.handleRightClick);

    // The socket is an accelerator, not the source of truth: a dropped or raced
    // new_cycle event would otherwise strand the player in a stale phase.
    this.cycleSyncTimer = setInterval(this.reconcileCycle, CYCLE_RECONCILE_MS);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    this.unsubscribeBus();
    this.unsubscribeFightClock();

    // Remove global event listeners
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    window.removeEventListener('contextmenu', this.handleRightClick);
    window.removeEventListener('resize', this.measureFightRail);

    if (this.cycleSyncTimer) clearInterval(this.cycleSyncTimer);
    if (this.fightSidesRefreshTimer) clearTimeout(this.fightSidesRefreshTimer);
    if (this.fightStatusRetryTimer) clearTimeout(this.fightStatusRetryTimer);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.documentElement.classList.remove('game-signed');
  },
  computed: {
    /* What the bars should show as about to be spent. An armed action outranks
       a hovered one: having committed, the player wants to keep seeing the
       price of the thing they are holding while the cursor wanders the grid.

       Both game modes read this — the world view and the tactical board share
       one action bar and one set of resource bars. */
    pendingCost() {
      return this.selectedAction?.cost || this.hoveredAction?.cost || [];
    },
    /* Ground that refuses violence. `Position.is_safe` is a property of the
       place, and the server refuses every offensive action taken on it
       (`apps/game/services/action/safe_zone.py`) — so the bar greys those
       abilities out rather than offering a blow that will bounce. A fight
       already open here is the one exception the server makes, and the only
       fight that can open on safe ground is a duel both sides accepted. */
    inSafeZone() {
      return Boolean(this.position?.is_safe) && !this.isInFight;
    },
    actionRefusalTitle() {
      return this.actionRefusalCode === 'SafeZoneViolation'
        ? this.$t('actionRefusal.safeZoneTitle')
        : this.$t('actionRefusal.title');
    },
    /* Everyone has acknowledged but the server has not opened the next phase
       yet — the dial holds rather than counting down past a stall. */
    fightResolving() {
      const participants = this.fightClock?.participants || [];
      const total = this.fightClock?.participant_count ?? participants.length;
      const ended = this.fightClock?.ended_turn_count ?? 0;
      return total > 0 && ended >= total;
    },
    /* `/game/training` and `/game` are the same view over two different acting
       characters; the route is the only thing that tells them apart. */
    isTrainingRoute() {
      return !!this.$route?.meta?.training;
    },
    inTacticalMode() {
      return this.playerGeneralInfo?.screen === 'tactical';
    },
    isActingSigned() {
      return Boolean(this.playerGeneralInfo?.game_master_mode);
    },
    isGameMaster() {
      return this.$store.getters.isGameMaster === true;
    },
    isTacticalMovementPhase() {
      // Show tactical movement icon when:
      // 1. In tactical mode (on tactical map)
      // 2. In PREPARATION phase (when tactical movement is allowed)
      // 3. No action selected (handled in ActionTrigger component)
      // 4. Tactical movement not disabled by player
      return this.inTacticalMode &&
             this.effectivePhase?.toLowerCase() === 'preparation' &&
             !this.tacticalMovementDisabled;
    },
    inTacticalPreparationButDisabled() {
      // True when in tactical mode, preparation phase, but movement has been ended
      // Used to show empty state instead of "End Turn" button
      return this.inTacticalMode &&
             this.effectivePhase?.toLowerCase() === 'preparation' &&
             this.tacticalMovementDisabled;
    },
    currentBargainId() {
      /* The record this character opened, when they opened it. The other side
         never saw the id, so they fall back to the one open table they have —
         a character carries at most one. */
      const open = this.bargains || [];
      if (this.selectedBargainId && open.some(b => b.id === this.selectedBargainId)) {
        return this.selectedBargainId;
      }
      return open[0]?.id;
    },
    /*
     * A fight owns its own board (`FightState.tactical_map`), while a duellist
     * keeps their real world Position — so the two ids disagree for exactly the
     * duration of a duel. Preferring the fight's map is what lets the arena
     * render through the ordinary tactical path instead of a view of its own.
     */
    activeTacticalMapId() {
      return this.fightClock?.tactical_map || this.position?.tactical_map?.id || null;
    },
    /*
     * A fight detaches from the world cycle, which then stops ticking for the
     * participant — `currentCyclePhase` freezes at whatever the world last
     * broadcast. Every ability, casting and tactical-movement gate must read
     * the fight's own phase instead, or the whole action bar stays disabled
     * for the length of the fight. Outside a fight this is the world phase
     * unchanged.
     */
    effectivePhase() {
      return this.isInFight && this.fightClock
          ? this.fightClock.phase
          : this.currentCyclePhase;
    },
    showFightRail() {
      return this.isInFight && !!this.fightClock && !this.hasPendingFight;
    },
    /* Whether this character is looking at the tactical board rather than the
       world view. A fight is not always tactical, so several controls key off
       this rather than off `isInFight`. */
    isTacticalScreen() {
      return this.playerGeneralInfo?.screen === 'tactical';
    },
    /* Self has acknowledged, the phase has not turned over yet. */
    isWaitingForOpponents() {
      if (!this.isInFight || !this.fightClock) return false;
      const me = this.playerGeneralInfo?.id;
      if (!me) return false;
      return (this.fightClock.ended_turns || []).includes(me);
    },
    /* This character's own row in the fight's roster — the only place the
       client learns whether it is still standing. */
    selfFightParticipant() {
      const me = this.playerGeneralInfo?.id;
      if (!me || !this.fightClock) return null;
      return (this.fightClock.participants || []).find(p => p.character_id === me) || null;
    },
    /*
     * A death save arrives as an ordinary `challenge_created`, so what makes
     * one a death save is not the challenge but the server saying a save of
     * ours is waiting on the dice this round. Deriving it from the fight state
     * rather than from the challenge's wording keeps the roller honest in any
     * locale.
     *
     * Being down is not enough on its own. The server refuses to displace a
     * challenge a downed character already holds, and writes no save that
     * round — dressing that unrelated roll up as a death save would put the
     * title, the difficulty framing and "not rolling counts as a failure" over
     * a roll that cannot fail them.
     */
    deathSaveProgress() {
      const me = this.selfFightParticipant;
      if (!me || me.standing !== false) return null;
      const progress = me.death_save;
      return progress && progress.pending ? progress : null;
    },
    deathSaveFailuresToComa() {
      return this.fightClock?.death_save_failures_to_coma ?? 3;
    },
    deathSaveSuccessesToRecover() {
      return this.fightClock?.death_save_successes_to_recover ?? 3;
    },
    pendingChallenge() {
      return this.pendingChallenges[0] || null;
    },
    /* True only while pending and unassigned, per `FightState.may_choose_
       side` — the same flag the server uses to decide whether the
       choose/create-side routes are open at all. */
    hasSideChoice() {
      return this.hasPendingFight && !!this.fightClock?.may_choose_side;
    },
    /* The donut cannot draw a fight whose sides have not arrived yet. */
    hasFightSides() {
      return (this.fightClock?.sides || []).length > 0;
    },
    canChallengeSelected() {
      return !this.isInFight
          && !this.hasPendingFight
          && !this.pendingChallenge
          && !!this.selectedGameObjectId
          && this.selectedGameObjectId !== this.playerGeneralInfo?.id;
    },
    centerAreaNotInteractive() {
      return !this.isDiceVisible && !this.isInventoryVisible;
    },
    positionSummary() {
      if (!this.position) return '—';
      const facts = [this.$t(this.position.is_safe ? 'codexRail.safe' : 'codexRail.open')];
      // Saying "safe zone" and nothing else left the greyed-out attacks
      // looking broken rather than forbidden.
      if (this.inSafeZone) facts.push(this.$t('codexRail.peaceHeld'));
      if ((this.position.anomalies || []).length) facts.push(this.$t('codexRail.anomaly'));
      if (this.position.tactical_map) facts.push(this.$t('codexRail.tactical'));
      return facts.join(' · ');
    },
    isCompassVisible() {
      // Hide compass during ACTION phase - only show in PREPARATION phase
      const isPreparationPhase = this.currentCyclePhase?.toLowerCase() === 'preparation';
      return this.hasActionPoints && !this.isDiceVisible && !this.isInventoryVisible && !this.bargainVisible && !this.isInFight && !this.hasPendingFight && !this.isTacticalScreen && isPreparationPhase;
    },
    isDiceVisible() {
      return this.diceVisible;
    },
    anyModalOpen() {
      return this.diceVisible || this.inventoryVisible || this.longRestVisible ||
             this.playerProfileVisible || this.openChestId != null;
    },
    isBargainVisible() {
      return this.bargainVisible && this.bargains?.length > 0;
    },
    isInventoryVisible() {
      return this.inventoryVisible;
    },
    availableActions() {
      if (!this.playerSkills) return [];

      return this.playerSkills.map((knownSkill) => {
        return knownSkill.skill;
      });
    },
    availableItems() {
      if (!this.inventoryItems) return [];

      return this.inventoryItems
          .filter(
              (word_item) =>
                  word_item.item.skill !== null &&
                  word_item.charges_left > 0
          );
    },
    availableGameObjects() {
      // Return a list of game objects that are available to the player in current location
      // Players, Items, etc.
      const playersGameObjects = this.characters.map((char) => {
        return {
          id: char.id,
          name: char.name,
          icon: char.biography?.avatar || '',
        };
      });
      const npcGameObjects = this.npcCharacters.map((char) => {
        return {
          id: char.id,
          name: char.name,
          icon: char.biography?.avatar || '',
        };
      });
      const itemsGameObjects = [];
      return playersGameObjects.concat(itemsGameObjects).concat(npcGameObjects);
    },
    hasActionPoints() {
      return this.playerService?.hasActionPoints();
    },
    activeConnections() {
      if (!this.connections) return [];
      console.log(this.connections);
      // this.connections is an object, so we need to convert it to an array
      return Object.values(this.connections);
    },
  },
  async created() {
    try {
      // Initialize the CharacterCurrentLocationService
      await CharacterCurrentLocationService.initialize();

      await this.updateAll();
      /*
       * The log is a rolling window over the last few cycles, so a fresh page load
       * arrives holding a round that has already been fought. Seen, not replayed.
       */
      this.$refs.tacticalMap?.markReelSeen?.(this.actionLog);
      // Additional check for fight status after component is created
      if (this.playerInfo) {
        await this.checkFightStatus();
      }
    } catch (error) {
      console.error("Error during component creation:", error);
      // Initialize with default values to prevent errors
      this.position = {id: null, image: null, anomalies: [], connections: [], characters: []};
      this.connections = {};
      this.characters = [];
      this.npcCharacters = [];
      this.playerInfo = {id: null, coordinates: null, attributes: []};
      this.playerGeneralInfo = {biography: {avatar: null}};
      this.playerSkills = [];
      this.activeEffects = [];
      this.shields = [];
      this.playerSpecials = [];
      this.mapData = {};
      this.actionLog = [];
      this.inventoryItems = [];
      this.bargains = [];
      this.selectedBargainId = null;
      this.isInFight = false;
      this.hasPendingFight = false;
    }
  },
  methods: {
    /*
     * One table, three callers. `reconfigureWebSocketForNewCharacter` used to
     * re-register four of these by hand, so a GM switching character mid-fight
     * silently lost the six it forgot — including, once the clock arrived, the
     * fight's own phase tick.
     */
    busHandlers() {
      return [
        ["world::new_cycle", this.handleCycleChange],
        ["character::challenge_created", this.handleChallengeCreated],
        ["world::character_changed", this.handleCharacterChanged],
        ["character::screen_mode_changed", this.handleScreenModeChanged],
        ["fight::fight_ended", this.handleFightLifecycleChanged],
        ["fight::pending_join_fight", this.handleFightLifecycleChanged],
        ["fight::joined_fight", this.handleFightLifecycleChanged],
        ["fight::left_fight", this.handleFightLifecycleChanged],
        ["character::fight_invitation_received", this.handleFightInvitationReceived],
        ["character::fight_invitation_answered", this.handleFightInvitationAnswered],
      ];
    },
    subscribeBus() {
      if (!this.bus) return;
      /* off-then-on so a re-subscribe after a reconnect cannot double-fire. */
      this.busHandlers().forEach(([event, handler]) => {
        this.bus.off(event, handler);
        this.bus.on(event, handler);
      });
      this.attachFightClock();
    },
    unsubscribeBus() {
      if (!this.bus) return;
      this.busHandlers().forEach(([event, handler]) => this.bus.off(event, handler));
      this.fightService.detach();
    },
    attachFightClock() {
      if (!this.bus) return;
      const characterId = this.playerGeneralInfo?.id || this.playerInfo?.id;
      if (!characterId) return;
      this.fightService.attach(this.bus, characterId);
    },
    fightClockHandlers() {
      return [
        ['roundAdvanced', this.handleFightRoundAdvanced],
        ['turnEnded', this.handleFightTurnEnded],
        ['fightEnded', this.handleFightEnded],
        ['endVoteChanged', this.handleFightEndVoteChanged],
        ['turnConflict', this.syncFightClock],
        /* Establishment is the one phase where the state changes because of
           what OTHER people do — a side opens, a fighter picks one — and the
           screen is nothing but a picture of that. Without these three the
           donut only ever redrew for the viewer's own actions. */
        ['sideCreated', this.handleFightSidesChanged],
        ['sideUpdated', this.handleFightSidesChanged],
        ['participantSideChanged', this.handleFightSidesChanged],
      ];
    },
    subscribeFightClock() {
      this.fightClockHandlers().forEach(([event, handler]) => this.fightService.on(event, handler));
    },
    unsubscribeFightClock() {
      this.fightClockHandlers().forEach(([event, handler]) => this.fightService.off(event, handler));
    },
    getCharacters() {
      return CharacterCurrentLocationService.getAllCharacters();
    },
    refreshCharactersFromService() {
      try {
        // Update characters and npcCharacters from the service
        this.characters = CharacterCurrentLocationService.getPlayerCharacters();
        this.npcCharacters = CharacterCurrentLocationService.getNpcCharacters();
      } catch (error) {
        console.error("Error refreshing characters from service:", error);
      }
    },
    /*
     * A character whose own screen says `join_fight` but whose fight or clock
     * would not read back yet.
     *
     * `checkFightStatus` is edge-triggered: the two founders of a fight are
     * told about it by exactly one `character::screen_mode_changed`, and if the
     * reads it provokes come back empty the flag is written false and nothing
     * asks again until the next world cycle — a fight the other player is
     * already looking at, invisible here for up to a cycle. The server now
     * holds that event until its transaction commits, so the reads should
     * succeed; this is the belt to that pair of braces, and it covers every
     * other way a read can lose the race (a reconnect mid-open, a slow
     * placement, a dropped publication).
     */
    fightStatusIsStale() {
      const wants = this.playerGeneralInfo?.screen === 'join_fight'
          && !!(this.playerInfo?.fight || this.playerGeneralInfo?.fight);
      return wants && !this.hasPendingFight;
    },
    /* Bounded: five tries over ~6s, then the world cycle has it. */
    scheduleFightStatusRetry() {
      if (this.fightStatusRetryTimer) return;
      if (this.fightStatusRetries >= 5) return;
      this.fightStatusRetries += 1;
      const delay = 400 * this.fightStatusRetries;
      this.fightStatusRetryTimer = setTimeout(() => {
        this.fightStatusRetryTimer = null;
        this.checkFightStatus();
      }, delay);
    },
    clearFightStatusRetry() {
      if (this.fightStatusRetryTimer) clearTimeout(this.fightStatusRetryTimer);
      this.fightStatusRetryTimer = null;
      this.fightStatusRetries = 0;
    },
    async checkFightStatus() {
      if (!this.playerInfo) {
        this.isInFight = false;
        this.hasPendingFight = false;
        this.resetFightData();
        this.clearFightClock();
        this.clearFightStatusRetry();
        return;
      }

      try {
        this.isInFight = await this.fightService.isInFight(this.playerInfo);
        if (this.isInFight) {
          // Refresh fight data to ensure we have the latest information
          const fight = await this.fightService.refreshFight(this.playerInfo);

          // A closed fight is not a fight — the client used to stay pinned to it.
          if (!fight || fight.ended_at || fight.open === false) {
            this.isInFight = false;
            this.hasPendingFight = false;
            this.resetFightData();
            this.clearFightClock();
            if (this.fightStatusIsStale()) this.scheduleFightStatusRetry();
            return;
          }

          await this.updateFightData();

          // Check if character is in join_fight mode or pending join
          const isInJoinFightMode = this.playerGeneralInfo?.screen === 'join_fight';
          const isPendingJoin = await this.fightService.isPendingJoin(this.playerInfo);

          // The clock is loaded BEFORE the flag that renders it. The sides
          // arrive with the clock, and every fight is opened with two of them
          // (`FightOpener.seat_founders`), so a pending flag that ran ahead of
          // the load left the establishment screen with nothing to draw for
          // the length of a round trip.
          await this.refreshFightClock();
          this.hasPendingFight = (isInJoinFightMode || isPendingJoin) && this.hasFightSides;
        } else {
          this.hasPendingFight = false;
          this.resetFightData();
          this.clearFightClock();
        }
        if (this.fightStatusIsStale()) {
          this.scheduleFightStatusRetry();
        } else {
          this.clearFightStatusRetry();
        }
      } catch (error) {
        console.error("Error checking fight status:", error);
        this.isInFight = false;
        this.hasPendingFight = false;
        this.resetFightData();
        this.clearFightClock();
        if (this.fightStatusIsStale()) this.scheduleFightStatusRetry();
      }
    },

    async updateFightData() {
      try {
        const [locationName, round] = await Promise.all([
          this.fightService.getPositionName(this.playerInfo),
          this.fightService.getCurrentRound(this.playerInfo)
        ]);

        this.fightLocationName = locationName;
        this.fightRound = round;
      } catch (error) {
        console.error("Error updating fight data:", error);
        this.resetFightData();
      }
    },

    resetFightData() {
      this.fightLocationName = null;
      this.fightRound = null;
    },

    /*
     * Fight clock
     * -----------
     * Everything below is inert outside a fight: `fightClock` stays null, the
     * rail never renders, and `handleActionTriggerClick` keeps taking the world
     * branch it always took.
     */

    /** Copy the service's authoritative clock onto the view. */
    syncFightClock() {
      this.fightClock = this.fightService.state;
      this.fightSkewMs = this.fightService.currentSkewMs;
      this.$nextTick(this.measureFightRail);
    },

    /*
     * A side opened, changed shape, or somebody moved onto one.
     *
     * The local state the service keeps is enough to redraw immediately — it
     * upserts the side and rewrites the participant's `side_id` — so the
     * screen updates on the event rather than waiting on a round trip. But a
     * fighter who was pulled into the fight after this client last loaded the
     * clock is on NO participant row here, and no patch can invent one, so a
     * full read follows. It is coalesced because these three events arrive
     * together in bursts: one side_updated per side plus a participant move
     * for every choice anybody makes.
     */
    handleFightSidesChanged() {
      this.syncFightClock();
      if (this.fightSidesRefreshTimer) return;
      this.fightSidesRefreshTimer = setTimeout(async () => {
        this.fightSidesRefreshTimer = null;
        try {
          await this.fightService.loadCurrentClock();
          this.syncFightClock();
        } catch (error) {
          if (error?.response?.status !== 404) {
            console.error('[Location] Could not reload the fight clock:', error);
          }
        }
      }, 300);
    },

    clearFightClock() {
      this.fightService.detach();
      this.fightClock = null;
      this.fightSkewMs = 0;
      this.fightSafeTop = 0;
    },

    async refreshFightClock() {
      if (!this.isInFight) {
        this.clearFightClock();
        return;
      }
      try {
        await this.fightService.loadCurrentClock();
        this.attachFightClock();
        this.syncFightClock();
      } catch (error) {
        /* 404 is "no open fight for this character" — not a failure. */
        if (error?.response?.status !== 404) {
          console.error('[Location] Could not load the fight clock:', error);
        }
        this.clearFightClock();
      }
    },

    /* The board is framed inside what the rail leaves of the viewport. */
    measureFightRail() {
      this.fightSafeTop = this.$refs.fightRail?.offsetHeight || 0;
    },

    async handleFightRoundAdvanced(data) {
      if (!this.isForCurrentFight(data)) return;
      this.syncFightClock();
      /* A new phase re-opens tactical movement, exactly as a world cycle did. */
      this.tacticalMovementDisabled = false;
      this.clearSelectedAction();
      await this.refreshForFightRound();
    },

    handleFightTurnEnded(data) {
      if (!this.isForCurrentFight(data)) return;
      this.syncFightClock();
    },

    async handleFightEnded(data) {
      if (!this.isForCurrentFight(data)) return;
      this.clearFightClock();
      await this.updateAll();
    },

    isForCurrentFight(data) {
      const trackedId = this.fightClock?.fight_id;
      if (!trackedId) return false;
      return !data?.fight_id || data.fight_id === trackedId;
    },

    /** Everything a fight round changes for this character. */
    async refreshForFightRound() {
      await Promise.all([
        this.getPlayerInfo(),
        this.getActiveEffects(),
        this.refreshShields(),
        this.refreshInventory(),
        this.refreshActionLog(),
        this.getPlayerSpecials(),
      ]);

      /*
       * A sealed fight ignores the world cycle, so `handleCycleChange` — the other
       * caller of the reel — returns early for the whole fight. Without this the
       * board never replays a round it fought.
       */
      this.$refs.tacticalMap?.playResolutionReel?.(this.actionLog, {trusted: this.actionLogTrusted});
    },

    /**
     * The deadline the server quoted has passed. The worker is what actually
     * advances the phase, so this only asks the server what the truth now is
     * rather than advancing anything itself.
     */
    async handleFightPhaseExpired() {
      await this.refreshFightClock();
    },

    async handleFightEndTurn() {
      if (!this.fightClock || this.endingFightTurn) return;
      this.endingFightTurn = true;
      try {
        const result = await this.fightService.endTurn(this.playerGeneralInfo);
        this.syncFightClock();
        if (result?.advanced) {
          await this.refreshForFightRound();
        }
      } catch (error) {
        console.error('[Location] Ending the fight turn failed:', error);
      } finally {
        this.endingFightTurn = false;
      }
    },

    openSurrenderConfirm() {
      this.surrenderConfirmOpen = true;
    },

    cancelSurrender() {
      this.surrenderConfirmOpen = false;
    },

    async confirmSurrender() {
      this.surrenderConfirmOpen = false;
      try {
        await this.fightService.surrender();
      } catch (error) {
        console.error('[Location] Surrender failed:', error);
      }
      this.clearFightClock();
      await this.updateAll();
    },

    /**
     * Breakthrough is a plain `CharacterAction`, not a fight-specific route,
     * so it goes through the same `ActionService` every other action does
     * rather than through `fightService`. `accept()` runs synchronously with
     * the request, so a fresh fight clock read right after it resolves
     * already carries `breakthrough_used: true` on this character's
     * participant row — the control's disabled state is driven by that DTO
     * field, not by any client-local bookkeeping.
     */
    /**
     * A hand-stepped world cycle changes almost everything on this screen —
     * effects ticked, actions resolved, NPCs moved, spawners fired — so the
     * view is re-read wholesale rather than guessing which parts moved.
     */
    async handleCycleAdvanced() {
      await this.refreshAndReplay();
    },

    openEndVoteConfirm(agree = true) {
      this.pendingEndVoteAgree = agree !== false;
      this.endVoteConfirmOpen = true;
    },

    cancelEndVote() {
      this.endVoteConfirmOpen = false;
    },

    /**
     * Cast (or withdraw) this character's vote to end the fight.
     *
     * Unlike forfeit this does not leave the fight on failure — a refused vote
     * changes nothing, and the player stays exactly where they were. The
     * clock is re-synced either way, because the tally the rail draws must be
     * the server's and not an optimistic guess.
     */
    async confirmEndVote() {
      this.endVoteConfirmOpen = false;
      if (!this.fightClock || this.castingEndVote) return;
      this.castingEndVote = true;
      try {
        await this.fightService.voteToEnd(this.pendingEndVoteAgree);
        this.syncFightClock();
      } catch (error) {
        console.error('[Location] The end-fight vote failed:', error);
        await this.refreshFightClock();
      } finally {
        this.castingEndVote = false;
      }
    },

    /* Somebody else voted or withdrew: the rail has to re-read the tally so a
       holdout can see that everyone is waiting on them. */
    handleFightEndVoteChanged(data) {
      if (!this.isForCurrentFight(data)) return;
      this.syncFightClock();
    },

    openEndTrainingConfirm() {
      this.endTrainingConfirmOpen = true;
    },

    cancelEndTraining() {
      this.endTrainingConfirmOpen = false;
    },

    /**
     * Tear the training down: the clone, the dummy and the fight go with it.
     * The real character was never bound to any of it, so there is nothing to
     * restore — the route simply ends, and the lobby is where it ends.
     */
    async confirmEndTraining() {
      this.endTrainingConfirmOpen = false;
      if (this.endingTraining) return;
      this.endingTraining = true;
      try {
        await endTraining();
      } catch (error) {
        console.error('[Location] Ending training failed:', error);
        this.endingTraining = false;
        this.endTrainingErrorMessage = this.$t('fightHud.endTrainingErrorGeneric');
        return;
      }
      this.clearFightClock();
      try {
        await this.$router.push({name: 'PlayerHomeDashboard'});
      } finally {
        this.endingTraining = false;
      }
    },

    /*
     * Duel challenges
     * ---------------
     * An invitation is an ordinary character action, so it goes through
     * ActionService like every other one; only the listing is a fight read.
     */

    async loadPendingChallenges() {
      try {
        const invitations = await this.fightService.listInvitations();
        /* Only invitations addressed to us are answerable, so without a known
           identity the honest answer is "none" rather than "all of them" —
           the ones this character SENT would otherwise ask to be accepted. */
        const me = this.playerGeneralInfo?.id || this.playerInfo?.id;
        /* The server sweeps invitations that ran out their TTL, but the
           listing is a snapshot: one can age out between the fetch and the
           draw, and answering it would only earn a refusal. */
        const now = Date.now();
        this.pendingChallenges = !me ? [] : (invitations || [])
            .filter(invitation => invitation.state === 'pending')
            .filter(invitation => invitation.invitee?.id === me)
            .filter(invitation => {
              const expiresAt = Date.parse(invitation.expires_at);
              return Number.isNaN(expiresAt) || expiresAt > now;
            })
            /* The whole `Fighter` is carried, not just the id: the overlay
               draws the inviter's portrait, rank and path from it. The name
               stays as its own derived field because `canChallengeSelected`
               and the invitation list are read by id, not by object. */
            .map(invitation => ({
              id: invitation.id,
              inviter: invitation.inviter || null,
              inviterId: invitation.inviter?.id || null,
              inviterName: invitation.inviter?.name || null,
              expiresAt: invitation.expires_at,
            }));
      } catch (error) {
        console.debug('[Location] Could not load duel challenges:', error?.message || error);
        this.pendingChallenges = [];
      }
    },

    async handleFightInvitationReceived(eventData) {
      if (!eventData?.invitee_id || eventData.invitee_id !== this.playerGeneralInfo?.id) {
        return;
      }
      await this.loadPendingChallenges();
    },

    async handleFightInvitationAnswered(eventData) {
      if (!eventData) return;
      const me = this.playerGeneralInfo?.id;
      if (eventData.inviter_id !== me && eventData.invitee_id !== me) {
        return;
      }
      this.dropChallenge(eventData.invitation_id);
      if (!eventData.accepted) {
        return;
      }
      /* Acceptance opens the arena. No router push: the character's screen mode
         and the fight's own map are all the tactical path needs. */
      await this.getPlayerInfo();
      await this.checkFightStatus();
    },

    dropChallenge(invitationId) {
      this.pendingChallenges = this.pendingChallenges.filter(c => c.id !== invitationId);
    },

    async issueChallenge() {
      if (this.issuingChallenge || !this.canChallengeSelected) return;
      this.issuingChallenge = true;
      try {
        await this.actionService.performAction({
          actionType: 'MAKE_DUEL_INVITATION',
          targets: [this.selectedGameObjectId],
        });
        await this.refreshActionLog();
      } catch (error) {
        console.error('[Location] Could not send the duel challenge:', error);
      } finally {
        this.issuingChallenge = false;
      }
    },

    async answerChallenge(challenge, actionType) {
      if (!challenge) return;
      try {
        await this.actionService.performAction({
          actionType,
          actionData: {invitation_id: challenge.id},
        });
        this.dropChallenge(challenge.id);
        await this.getPlayerInfo();
        await this.checkFightStatus();
        await this.refreshActionLog();
      } catch (error) {
        console.error(`[Location] ${actionType} failed:`, error);
        const detail = error?.response?.data;
        const message = Array.isArray(detail) ? detail[0] : (detail?.detail || detail?.error);
        this.$refs.challengeOverlay?.fail?.(message);
      }
    },

    handleChallengeAccept(challenge) {
      return this.answerChallenge(challenge, 'ACCEPT_DUEL_INVITATION');
    },

    handleChallengeReject(challenge) {
      return this.answerChallenge(challenge, 'REJECT_DUEL_INVITATION');
    },

    /* An invitation that ran out its TTL is already dead on the server, so the
       overlay is dismissed locally — no answer is sent for it. */
    handleChallengeDismiss(challenge) {
      if (!challenge) return;
      this.dropChallenge(challenge.id);
    },

    /*
     * Side selection
     * --------------
     * The donut performs no I/O of its own (`FightChallengeOverlay.vue`'s
     * precedent) — these two own the `choose_side`/`create_side` calls
     * through `FightService` and report the outcome back through the same
     * `fail()`/`reset()` shape the challenge overlay exposes.
     */
    async handleChooseSide(sideId) {
      try {
        await this.fightService.chooseSide(sideId);
        this.syncFightClock();
        this.$refs.sideDonut?.reset();
      } catch (error) {
        console.error('[Location] Could not choose a side:', error);
        const detail = error?.response?.data;
        const message = Array.isArray(detail) ? detail[0] : (detail?.detail || detail?.side_id?.[0]);
        this.$refs.sideDonut?.fail(message);
      }
    },

    async handleCreateSide() {
      try {
        await this.fightService.createSide();
        this.syncFightClock();
        this.$refs.sideDonut?.reset();
      } catch (error) {
        console.error('[Location] Could not create a side:', error);
        const detail = error?.response?.data;
        const message = Array.isArray(detail) ? detail[0] : (detail?.detail || detail?.name?.[0]);
        this.$refs.sideDonut?.fail(message);
      }
    },

    /*
     * The establishment countdown ran out. Nudge the clock rather than wait
     * for the fight worker: the worker is what normally expires a phase, but
     * it is off by default outside production, and a fight that reached its
     * establishment floor with everyone already acked would then sit there.
     *
     * Only when this viewer has no choice to make. `end_turn` for a character
     * the server already auto-acked writes nothing — it just re-runs the
     * readiness check, which is exactly the nudge wanted. Sending it for a
     * viewer who still could choose would spend their choice for them, and
     * the server's own deadline already handles that case.
     */
    async handleEstablishmentExpired() {
      if (this.hasSideChoice || !this.playerInfo) return;
      try {
        await this.fightService.endTurn(this.playerInfo);
        this.syncFightClock();
      } catch (error) {
        console.error('[Location] Could not settle the establishment phase:', error);
      }
    },

    handleActionHovered(preview) {
      this.hoveredAction = preview;
    },
    handleActionHoverEnd() {
      this.hoveredAction = null;
    },
    async handleSkillSelected(skill) {
      // Store selected action for UI display and execution
      this.selectedAction = {
        icon: skill.icon || '',
        name: skill.name,
        type: 'skill',
        skillType: skill.type || 'utility',
        cost: abilityAvailability.costOf(skill),
        data: skill,
      };

      // Check if in tactical mode - if so, use PlayerTacticalMap selection
      if (this.inTacticalMode && this.$refs.tacticalMap) {
        console.log('[Location] Skill selected in tactical mode:', skill.name);
        this.$refs.tacticalMap.selectSkill(skill);
        return; // Let tactical map handle execution
      }

      if (await this.castOnSelf(skill)) return;

      // Default mode: wait for target selection
      console.log('[Location] Skill selected in default mode, waiting for target selection:', skill.name);
    },
    async handleItemSelected(item) {
      // Store selected action for UI display and execution
      this.selectedAction = {
        icon: item.item?.icon || item.icon || '',
        name: item.item?.name || item.name,
        type: 'item',
        skillType: item.item?.skill?.type || 'utility',
        cost: abilityAvailability.costOf(item),
        data: item,
      };

      // Check if in tactical mode - if so, use PlayerTacticalMap selection
      if (this.inTacticalMode && this.$refs.tacticalMap) {
        console.log('[Location] Item selected in tactical mode:', item.name);
        this.$refs.tacticalMap.selectItem(item);
        return; // Let tactical map handle execution
      }

      if (await this.castOnSelf(item)) return;

      // Default mode: wait for target selection
      console.log('[Location] Item selected in default mode, waiting for target selection:', item.name);
    },
    async handleAbilityCast(abilityData) {
      console.log('[Location] Ability cast:', abilityData);

      const { ability, data } = abilityData;

      /* The phase an ability may be used in is the ability's own rule, not a
         blanket one: a special carries `allowed_phases` and Breakthrough is
         taken in preparation. Anything the phase forbids is already absent
         from what the server offered, so reaching here is a stale HUD. */
      if (!abilityAvailability.canUseAbility(ability, this.playerService, this.effectivePhase)) {
        console.warn('[Location] Ability not usable in the current phase:', this.effectivePhase);
        return;
      }

      // Check if we have tactical_aims array (new multi-charge format)
      if (!data || !data.tactical_aims || !Array.isArray(data.tactical_aims)) {
        console.error('[Location] Missing tactical_aims array in ability data:', abilityData);
        return;
      }

      console.log(`[Location] Processing ability with ${data.tactical_aims.length} charge(s):`, data.tactical_aims);

      // Determine action type and ability reference
      let actionType, skill, item;

      // Check ability type by examining its properties
      if (ability.school_id || ability.school) {
        // It's a skill (has school reference)
        actionType = "USE_SKILL";
        skill = ability.id;
        item = null;
      } else if (ability.item || ability.item_type || ability.quantity !== undefined) {
        // It's a world item (carries the nested item template) or a bare item
        actionType = "USE_ITEM";
        skill = null;
        item = ability.id;
      } else if (ability.action_type) {
        // It's a special action (has action_type)
        actionType = ability.action_type;
        skill = null;
        item = null;
      } else {
        console.error('[Location] Unknown ability type:', ability);
        return;
      }

      // Backend expects tactical_aims array (even for single charge)
      const action = {
        actionType: actionType,
        actionData: {
          tactical_aims: data.tactical_aims, // Array of {x, y, rotation} objects
        },
        skill: skill,
        item: item,
        targets: [], // Empty in tactical mode - backend resolves from tactical_aims
      };

      console.log('[Location] Submitting tactical ability action:', action);

      try {
        /* The ability object travels with the action: the submit response serializes
         * `skill` as a bare id and carries no impacts, so it is the only thing that
         * can tell the board a heal from a sword swing. */
        await this.applyAction(action, ability);
        console.log('[Location] Ability action submitted successfully');
        this.clearSelectedAction();
      } catch (error) {
        console.error('[Location] Error submitting ability action:', error);
        this.clearSelectedAction();
      }
    },
    async handleSpecialSelected(special) {
      // Store selected action for UI display and execution
      this.selectedAction = {
        icon: special.icon || '',
        name: special.name || special.action_type,
        type: 'special',
        skillType: 'special',
        cost: abilityAvailability.costOf(special),
        data: special,
      };

      if (this.inTacticalMode && this.$refs.tacticalMap) {
        this.$refs.tacticalMap.selectSpecial(special);
        return; // Let tactical map handle execution
      }

      if (await this.castOnSelf(special)) return;

      // Default mode: wait for target selection
      console.log('[Location] Special action selected in default mode, waiting for target selection:', special.name || special.action_type);
    },

    /**
     * An ability done to its own user is executed the moment it is picked.
     *
     * Long Rest, Breakthrough, Flow Accumulation and every zero-reach skill
     * resolve against the character using them, so the target strip has
     * nothing to offer for one: the player was left holding a selected
     * ability with no legal body to point it at. Returns whether it fired.
     */
    async castOnSelf(ability) {
      if (!isSelfTargetAbility(ability)) return false;
      const self = this.playerInfo?.id;
      if (!self) return false;
      await this.updateSelectedGameObjectId(self);
      return true;
    },
    triggerNoTargetFeedback() {
      // Trigger visual feedback for no hasTarget selected
      this.showNoTargetFeedback = true;
      // Reset the feedback after animation duration (1 second)
      setTimeout(() => {
        this.showNoTargetFeedback = false;
      }, 1000);
    },
    async executeSelectedAction() {
      console.log('[Location] executeSelectedAction called. selectedGameObjectId:', this.selectedGameObjectId);

      // Prevent concurrent execution
      if (this.isExecutingAction) {
        console.warn('[Location] Action already being executed, ignoring duplicate call');
        return;
      }

      this.isExecutingAction = true;

      try {
        if (!this.selectedAction || !this.selectedAction.data) {
          console.warn('[Location] No action to execute');
          return;
        }

        // Enhanced validation for target ID
        if (!this.selectedGameObjectId ||
            typeof this.selectedGameObjectId !== 'string' ||
            this.selectedGameObjectId.trim() === '') {
          console.error('[Location] Invalid target ID:', this.selectedGameObjectId);
          this.triggerNoTargetFeedback();
          return;
        }

        const actionType = this.selectedAction.type;
        const actionData = this.selectedAction.data;

        console.log('[Location] Executing selected action:', actionType, actionData.name || actionData.action_type);
        console.log('[Location] Constructing action with target ID:', this.selectedGameObjectId);

        let action;

        if (actionType === 'skill') {
          action = {
            actionType: "USE_SKILL",
            actionData: {},
            skill: actionData.id,
            item: null,
            targets: [this.selectedGameObjectId],
          };
          console.log('[Location] Skill action constructed:', JSON.stringify(action));
        } else if (actionType === 'item') {
          action = {
            actionType: "USE_ITEM",
            actionData: {},
            skill: null,
            item: actionData.id,
            targets: [this.selectedGameObjectId],
          };
        } else if (actionType === 'special') {
          action = {
            actionType: actionData.action_type,
            actionData: actionData.action_data || {},
            skill: null,
            item: null,
            targets: [this.selectedGameObjectId],
          };
        } else {
          console.error('[Location] Unknown action type:', actionType);
          this.clearSelectedAction();
          return;
        }

        // Final validation before sending
        if (actionType === 'skill' || actionType === 'item') {
          if (!action.targets || action.targets.length === 0 || !action.targets[0]) {
            console.error('[Location] Action has invalid targets:', action.targets);
            alert('Please select a valid target for this action.');
            this.clearSelectedAction();
            return;
          }
        }

        console.log('[Location] About to call applyAction with action:', JSON.stringify(action));
        console.log('[Location] Action targets array:', action.targets);
        await this.applyAction(action);
        this.clearSelectedAction();
      } catch (error) {
        console.error('[Location] Error executing action:', error);
        this.clearSelectedAction();
      } finally {
        this.isExecutingAction = false;
      }
    },
    clearSelectedAction() {
      this.selectedAction = null;
      this.selectedGameObjectId = null; // Clear target selection too
      this.highlightedCharacterIds = []; // Clear character highlighting
      // Reset charge state
      this.chargesPlaced = 0;
      this.chargeCount = 1;
      if (this.$refs.tacticalMap) {
        this.$refs.tacticalMap.cancelSkillSelection();
      }
    },
    handleChargesUpdated(data) {
      // Update charge state from PlayerTacticalMap
      this.chargesPlaced = data.chargesPlaced;
      this.chargeCount = data.chargeCount;
      console.log('[Location] Charges updated:', data.chargesPlaced, '/', data.chargeCount);
    },
    handleCellHovered(characterIds) {
      // Update highlighted character IDs when hovering over cells in tactical mode
      this.highlightedCharacterIds = characterIds || [];
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') this.reconcileCycle();
    },
    async reconcileCycle() {
      /* A sealed fight runs on its own clock; correcting drift against the
         world cycle here would tear the fight's phase out from under it. */
      if (this.isInFight) return;
      try {
        const cycle = (await ActionGameApi.actionCurrentCycleRetrieve()).data;
        if (!cycle || !cycle.number) return;
        if (cycle.number === this.currentCycleNumber && cycle.phase === this.currentCyclePhase) return;

        console.debug('[Location] Cycle drift corrected', {
          was: { number: this.currentCycleNumber, phase: this.currentCyclePhase },
          now: { number: cycle.number, phase: cycle.phase },
        });
        this.currentCycleNumber = cycle.number;
        this.currentCyclePhase = cycle.phase;
        if (this.tacticalMovementDisabled) this.tacticalMovementDisabled = false;
        await this.refreshAndReplay();
      } catch (error) {
        console.debug('[Location] Cycle reconciliation skipped:', error?.message || error);
      }
    },
    /*
     * The one place a fight diverges from the world. `isInFight` is the only
     * distinguisher: outside a fight this is the same two-branch dispatcher it
     * has always been, which `component/locationFightRouting.spec.js` pins.
     */
    handleActionTriggerClick() {
      if (this.selectedAction) {
        this.clearSelectedAction();
        return;
      }
      if (this.isInFight) {
        return this.handleFightEndTurn();
      }
      return this.handleEndTurn();
    },
    async handleEndTacticalMove() {
      this.tacticalMovementDisabled = true;
      // In a fight, moving is the whole of the preparation phase, so finishing
      // your move finishes your turn. Without this the phase only ever ended on
      // its deadline: the player had said they were done and the clock ignored
      // them. Outside a fight this stays purely local — the world cycle has its
      // own end-turn and its own pace.
      if (this.isInFight) {
        await this.handleFightEndTurn();
      }
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.selectedAction) {
        event.preventDefault();
        this.clearSelectedAction();
      }
    },
    handleRightClick(event) {
      if (this.selectedAction) {
        event.preventDefault();
        this.clearSelectedAction();
      }
    },
    async handleCycleChange(data) {
      try {
        console.log("Cycle change event received", data);
        if (!data || !data.number) {
          console.error("Invalid cycle data received:", data);
          return;
        }

        // Only react to cycles in current campaign
        if (!data.is_current) {
          console.debug("Cycle is not current, skipping update");
          return;
        }

        // Sealed from the world clock: the fight's own round drives this view
        // while it lasts, and a world cycle here would reset the phase.
        if (this.isInFight) {
          console.debug("In a fight, world cycle ignored");
          return;
        }

        if (data.number === this.currentCycleNumber && data.phase === this.currentCyclePhase) {
          console.debug("Cycle number and phase are the same, skipping update");
          return;
        }
        this.currentCycleNumber = data.number;
        this.currentCyclePhase = data.phase;

        // Reset tactical movement disabled flag when cycle or phase changes
        if (this.tacticalMovementDisabled) {
          console.log('[Location] Resetting tactical movement for new cycle/phase');
          this.tacticalMovementDisabled = false;
        }

        await this.refreshAndReplay();
      } catch (error) {
        console.error("Error handling cycle change:", error);
      }
    },
    async fetchChallengeWithRetry(challengeId, attempts = 4) {
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          const response = await DiceGameApi.diceChallengeRetrieve(challengeId);
          if (response?.data) return response.data;
        } catch (error) {
          const status = error?.response?.status;
          if (status && status !== 404) throw error;
        }
        await new Promise(resolve => setTimeout(resolve, 150 * (attempt + 1)));
      }
      return null;
    },
    async handleChallengeCreated(challengeData) {
      try {
        if (!challengeData) {
          console.error("Invalid challenge data received:", challengeData);
          return;
        }
        console.log("Challenge created event received", challengeData);

        // The event can outrun replication, so give the read a couple of retries
        // rather than silently dropping the challenge.
        const challenge = await this.fetchChallengeWithRetry(challengeData.id);
        if (!challenge) {
          console.error("Challenge could not be fetched:", challengeData.id);
          return;
        }

        console.log("Challenge fetched successfully:", challenge);

        /* A death save arrives as an ordinary challenge and only the fight
           clock says it is one, so the clock has to be settled BEFORE the
           roller mounts. Opening first dressed a save as an ordinary check
           until the next clock event landed. */
        if (this.isInFight) {
          try {
            await this.fightService.loadCurrentClock();
            this.syncFightClock();
          } catch (error) {
            if (error?.response?.status !== 404) {
              console.error('[Location] Could not settle the fight clock for the challenge:', error);
            }
          }
        }

        // Store the challenge and open the dice modal
        this.currentChallenge = challenge;
        await this.openDice();

      } catch (error) {
        console.error("Error handling challenge created event:", error);
      }
    },
    async handleCharacterChanged(characterData) {
      try {
        if (!characterData || !characterData.id) {
          console.error("Invalid character data received:", characterData);
          return;
        }

        // Only update if this is the current player's character
        if (!this.playerGeneralInfo || characterData.id !== this.playerGeneralInfo.id) {
          console.debug("Character change event for different character, skipping");
          return;
        }

        console.log("Character changed event received for current player:", characterData);

        // Update playerInfo with new attribute data
        if (characterData.attributes) {
          this.playerInfo = {
            ...this.playerInfo,
            attributes: characterData.attributes,
            coordinates: characterData.coordinates,
          };
        }

        // Fetch full character data to update playerGeneralInfo
        const updatedCharacter = (await CharacterGameApi.characterPlayerRetrieve(characterData.id)).data;
        this.playerGeneralInfo = updatedCharacter;

        // Wait for Vue's reactivity to process
        await this.$nextTick();

        // Re-check fight status when character changes
        // This will update hasPendingFight and isInFight based on new screen mode
        await this.checkFightStatus();

        console.log("Character data updated, screen mode:", this.playerGeneralInfo.screen);

      } catch (error) {
        console.error("Error handling character changed event:", error);
      }
    },
    async handleFightLifecycleChanged(eventData) {
      try {
        const mine = eventData?.character_id
            ? eventData.character_id === this.playerGeneralInfo?.id
            : eventData?.fight_id && eventData.fight_id === this.playerInfo?.fight;
        if (!mine) {
          return;
        }
        await this.getPlayerInfo();
        await this.checkFightStatus();
      } catch (error) {
        console.error("Error handling fight lifecycle event:", error);
      }
    },
    async handleScreenModeChanged(eventData) {
      try {
        if (!eventData || !eventData.character_id) {
          console.error("Invalid screen mode change data received:", eventData);
          return;
        }

        // Only update if this is the current player's character
        if (!this.playerGeneralInfo || eventData.character_id !== this.playerGeneralInfo.id) {
          console.debug("Screen mode change event for different character, skipping");
          return;
        }

        console.log("Screen mode changed event received:", eventData);

        // Update the screen mode directly for immediate UI response
        if (this.playerGeneralInfo) {
          this.playerGeneralInfo.screen = eventData.new_mode;
        }

        // Mirror the fight reference, including its removal. The event carries a
        // null fight_id when the fight ends; skipping that case left the client
        // pinned to a closed fight with no way back to the location view.
        const fightId = eventData.fight_id ?? null;
        if (this.playerInfo) {
          this.playerInfo.fight = fightId;
        }
        if (this.playerGeneralInfo) {
          this.playerGeneralInfo.fight = fightId;
        }

        // Wait for Vue's reactivity to process
        await this.$nextTick();

        // Re-check fight status to update UI
        await this.checkFightStatus();

        console.log("Screen mode updated to:", eventData.new_mode, "Fight pending:", this.hasPendingFight);

      } catch (error) {
        console.error("Error handling screen mode changed event:", error);
      }
    },
    async refreshBargains() {
      try {
        this.bargains = await bargainService.getOpenBargains();
      } catch (error) {
        console.error("Error refreshing bargains:", error);
        this.bargains = [];
      }
      /* A bargain only lists for a side that has not answered it yet, so an
         entry here is an offer waiting on this character — including one the
         other side opened, which arrives with no action result to react to. */
      if (this.bargains?.length && !this.bargainVisible && !this.anyModalOpen) {
        this.selectedBargainId = null;
        await this.refreshInventory();
        this.bargainVisible = true;
      }
    },
    /**
     * The log, refetched — and whether it may be believed.
     *
     * A failed fetch used to empty the log, which the board then read as every action
     * in it having been refused. The previous window is kept instead and the trust
     * flag says it is stale, so nothing downstream mistakes silence for an answer.
     */
    async refreshActionLog() {
      try {
        const response = (await ActionGameApi.actionLogList()).data;
        // Extract the results array from the paginated response
        this.actionLog = response.results || [];
        this.actionLogTrusted = true;
      } catch (error) {
        console.error("Error refreshing action log:", error);
        this.actionLogTrusted = false;
      }
      return this.actionLogTrusted;
    },
    async refreshMiniMap() {
      try {
        this.mapData = (await WorldGameApi.worldMapsMiniMapRetrieve()).data;
      } catch (error) {
        // 403 is the expected answer while the character carries no Cartograph
        // artifact, and 400 while it belongs to an organization without a map.
        const status = error?.response?.status;
        if (status === 403 || status === 400) {
          console.debug("Mini map unavailable:", error.response?.data?.detail ?? error.response?.data);
        } else {
          console.debug("Problem refreshing mini map:", error);
        }
        this.mapData = {};
      }
    },
    resetAdditionalPlayerData() {
      console.log("resetting additional characters data");
      this.additionalCharactersData = {};
    },
    updateAdditionalPlayerData(characters) {
      console.debug("updating additional characters data", {characters});
      if (!this.additionalCharactersData) {
        this.additionalCharactersData = {};
      }

      // Iterate through the characters array
      characters.forEach(character => {
        const {id, name, attributes, dimension, rank_grade, path} = character;
        console.debug("Processing character:", {id, name, attributes, dimension, rank_grade, path});
        // Prepare the formatted data for the character
        const formattedData = {
          name: name || "xx",
          rankGrade: rank_grade !== undefined ? rank_grade : "xx",
          dimension: dimension !== undefined ? dimension : "xx",
          path: path || {name: "xx", icon: null},
          attributes: {}
        };

        // Process attributes into a key-value structure
        if (Array.isArray(attributes)) {
          console.debug("Processing attributes:", attributes);
          attributes.forEach(attr => {
            formattedData.attributes[attr.name.toLowerCase()] = {
              current: attr.current !== undefined ? attr.current : "xx",
              max: attr.max !== undefined ? attr.max : "xx"
            };
          });
        }
        console.debug("Formatted data:", formattedData, "for character:", id);
        // Store the formatted data in resetAdditionalPlayerData
        this.additionalCharactersData[id] = formattedData;
      });
    },
    async hideAll() {
      // Set all visibility flags to false
      // Using Vue.nextTick to ensure DOM updates are complete before continuing
      return new Promise(resolve => {
        this.diceVisible = false;
        this.inventoryVisible = false;
        this.bargainVisible = false;
        this.longRestVisible = false;
        this.$nextTick(() => {
          resolve();
        });
      });
    },
    /*
     * Travel state is derived, not remembered. It used to be a local flag set
     * when the player pressed a direction, so a page reload mid-journey landed
     * them back in the location as though they had never left. The character is
     * in transit exactly while an unperformed MOVE sits in the current cycle,
     * which the server already knows — so ask it.
     */
    async syncMovementState() {
      try {
        const {data} = await ActionGameApi.actionScheduledActionsRetrieve();
        const actions = Array.isArray(data) ? data : [data].filter(Boolean);
        const mine = this.playerInfo?.id || this.playerGeneralInfo?.id;
        this.isMoving = actions.some(
            (a) => a
                && !a.performed
                && a.initiator === mine
                && MOVEMENT_ACTION_TYPES.includes(a.action_type)
        );
      } catch (error) {
        /* A failed poll must not strand the player behind the overlay. */
        console.warn('Could not sync movement state', error);
        this.isMoving = false;
      }
    },
    async toogleDice() {
      if (this.diceVisible) {
        await this.closeDice();
      } else {
        await this.openDice();
      }
    },
    async openDice() {
      /* Opening a roller that is already open used to close it first — the
         chamber was torn out of the DOM and built again, which reads as the
         roller flashing shut and re-opening, and throws away a roll in
         flight along with the room it was being rolled in. */
      if (this.diceVisible) return;
      await this.hideAll();
      this.diceVisible = true;
    },
    async closeDice() {
      // Check if there's a current challenge - don't close modal if challenge is present
      if (this.currentChallenge && this.playerGeneralInfo && this.playerGeneralInfo.challenge) {
        console.log("Cannot close dice modal - current challenge is active:", this.currentChallenge.id);
        return;
      }

      // Clear the current challenge when closing the modal to ensure fresh state on reopen
      this.currentChallenge = null;
      this.diceVisible = false;
    },
    async openBargain(bargainId = null) {
      await this.refreshBargains();
      await this.refreshInventory();
      await this.hideAll();
      this.selectedBargainId = bargainId;
      this.bargainVisible = true;
    },
    async openLongRest(offerId = null) {
      await this.refreshInventory();
      await this.hideAll();
      this.currentLongRestOfferId = offerId;
      this.longRestVisible = true;
    },
    async closeLongRest() {
      this.longRestVisible = false;
      this.currentLongRestOfferId = null;
      await this.refreshInventory();
      await this.getPlayerInfo();
      await this.refreshActionLog();
    },
    onInventoryItemClicked(item) {
      console.debug("[Location] Inventory item clicked:", {item});
    },
    async toggleBargain() {
      await this.refreshInventory();
      if (this.bargainVisible) {
        this.bargainVisible = false;
        this.selectedBargainId = null;
      } else {
        this.bargainVisible = true;
      }
    },
    async toggleInventory() {
      if (this.inventoryVisible) {
        await this.closeInventory();
      } else {
        await this.openInventory();
      }
    },
    async openInventory() {
      await this.refreshInventory();
      // Not hiding all elements as per requirement to keep the right section visible
      this.inventoryVisible = true;
    },
    async closeInventory() {
      this.inventoryVisible = false;
    },
    async refreshInventory() {
      try {
        const response = await ItemsGameApi.itemsCharacterList();
        this.inventoryItems = response.data.map((item) => item.world_item);
      } catch (error) {
        console.error("Error refreshing inventory:", error);
        this.inventoryItems = [];
      }
    },
    async selectSelf() {
      if (this.playerInfo && this.playerInfo.id) {
        this.selectedGameObjectId = this.playerInfo.id;
      }
    },
    async refreshShields() {
      try {
        this.shields = (await ShieldsGameApi.shieldsActiveList()).data;
      } catch (error) {
        console.error("Error refreshing shields:", error);
        this.shields = [];
      }
    },
    async cancelAction() {
      this.selectedGameObjectId = null;
    },
    async handleAnomalyClick(anomalyId) {
      const action = {
        actionType: "ANOMALY",
        targets: [anomalyId],
      }
      console.debug("Anomaly clicked:", anomalyId, "Action to perform:", action);
      await this.applyAction(action);
    },
    async applyAction(action, ability = null) {
      try {
        const result = await this.actionService.performAction(action);
        await this.getPlayerInfo();

        // Ensure result is valid and handle based on action type
        if (!result || !result.action_type) {
          console.error("Invalid action result:", result);
          return;
        }
        console.debug("Action result:", {result});
        switch (result.action_type) {
          case "INSPECT":
            // Update additional player data if characters are present
            if (result.data?.characters?.length) {
              console.debug("result of inspection", {result});
              this.updateAdditionalPlayerData(result.data.characters);
            } else {
              console.warn("No characters data found in INSPECT action.");
            }
            break;
          case "BARGAIN":
            await this.openBargain(result.data?.bargain_id || null);
            break;
          case "LONG_REST":
            // The offer is written when the rest resolves on the next phase,
            // so its id is usually absent here and the panel polls for it.
            await this.openLongRest(result.data?.long_rest_offer_id || null);
            break;
          case "ANOMALY":
            // Handle anomaly action result
            console.debug("Anomaly action result:", {result});
            // Refresh player info and action log to reflect changes from anomaly interaction
            await this.getPlayerInfo();
            await this.refreshActionLog();
            break;
          default:
            console.log(`Unhandled action type: ${result.action_type}`);
            break;
        }
        // Refresh the action log after applying the action
        await this.refreshActionLog();

        /* An applied action is a submitted intent: it can still be refused and it only
         * lands when the phase closes. So the board holds the aim as pending and the
         * resolution reel is what draws it, once the server has actually resolved it. */
        if (holdsPendingAim(result)) {
          this.$refs.tacticalMap?.markActionPending?.(result, ability, {cycle: this.currentCycleNumber});
        }

        // Returned so a caller chaining a second action can tell whether the
        // first one actually landed — errors are swallowed here, so without it
        // "walk over and pick it up" would try to pick up from wherever the
        // character failed to leave.
        return result;
      } catch (error) {
        console.error("Error applying action:", error);
        this.showActionRefusal(error);
        return null;
      }
    },
    /* A refused action used to end at console.error, which meant a player saw
       nothing at all: no blow, no cost, no reason. The server's refusals carry
       `{code, message}` (`apps/game/exceptions/__init__.py`), and the message
       is written to be read. */
    showActionRefusal(error) {
      const detail = error?.response?.data;
      const payload = Array.isArray(detail) ? detail[0] : detail;
      const message = payload?.message || payload?.detail || payload?.error
        || (typeof payload === 'string' ? payload : null);
      if (!message) return;
      this.actionRefusalCode = payload?.code || null;
      this.actionRefusalMessage = message;
    },
    /* The bar refused the click itself — the server never heard about it, so
       there is no message to quote and the client states the rule. */
    refuseOffenceHere() {
      this.actionRefusalCode = 'SafeZoneViolation';
      this.actionRefusalMessage = this.$t('actionRefusal.safeZoneBody');
    },
    handleCharacterSelected(id) {
      console.log('[Location] Character selected:', id, 'Type:', typeof id);

      // Validate character ID before processing
      if (!id || typeof id !== 'string' || id.trim() === '') {
        console.error('[Location] Invalid character ID received:', id);
        return;
      }

      // In tactical mode the card strip and the map tokens are two views of the same
      // selection, so picking a card mirrors the selection onto the map. Casting stays
      // on the map's own targeting flow - going through updateSelectedGameObjectId here
      // would fire the non-tactical action path for an already tactically-aimed skill.
      // Clicking the selected card again clears the selection, unless an action is
      // armed - there the second click is the player confirming the target.
      const armed = Boolean(this.selectedAction && this.selectedAction.data);
      const deselect = !armed && this.selectedGameObjectId === id;

      if (this.inTacticalMode) {
        if (this.$refs.tacticalMap) {
          this.$refs.tacticalMap.selectObjectById(deselect ? null : id);
        }
        this.selectedGameObjectId = deselect ? null : id;
        return;
      }

      if (deselect) {
        this.selectedGameObjectId = null;
        return;
      }

      this.updateSelectedGameObjectId(id);
    },
    handleCardHovered(id) {
      this.highlightedCharacterIds = id ? [id] : [];
      if (this.inTacticalMode && this.$refs.tacticalMap) {
        this.$refs.tacticalMap.highlightObjectById(id);
      }
    },
    handleCardHoverLeave() {
      this.highlightedCharacterIds = [];
      if (this.inTacticalMode && this.$refs.tacticalMap) {
        this.$refs.tacticalMap.highlightObjectById(null);
      }
    },
    async updateSelectedGameObjectId(id) {
      console.log('[Location] Updating selectedGameObjectId to:', id);
      try {
        // Set the selected game object ID
        this.selectedGameObjectId = id;
        console.log('[Location] selectedGameObjectId set to:', this.selectedGameObjectId);

        // Wait for the next tick to ensure the state is updated
        await this.$nextTick();

        // If there's a selected action, execute it now
        if (this.selectedAction && this.selectedAction.data) {
          console.log('[Location] Executing action with target:', this.selectedGameObjectId);
          await this.executeSelectedAction();
        }
      } catch (error) {
        console.error("Error updating selected game object ID:", error);
        // Reset state in case of error
        this.selectedGameObjectId = null;
      }
    },
    async handleTacticalCellClick(cell) {
      console.log('[Location] Tactical cell clicked:', cell);

      // Check if tactical movement is disabled by player
      if (this.tacticalMovementDisabled) {
        console.warn('[Location] Tactical movement has been ended by player for this turn');
        return;
      }

      // Only allow tactical movement during PREPARATION phase (case-insensitive check)
      if (this.effectivePhase?.toLowerCase() !== 'preparation') {
        console.warn('[Location] Tactical movement only allowed in PREPARATION phase, current:', this.effectivePhase);
        return;
      }

      // Create TACTICAL_MOVE action with clicked cell coordinates
      const action = {
        actionType: "TACTICAL_MOVE",
        actionData: {
          tactical_aims: [{  // Backend expects array of tactical aims
            x: cell.x,
            y: cell.y,
            rotation: 0  // Default rotation (0 degrees)
          }]
        },
        skill: null,
        item: null,
        targets: [], // Tactical move doesn't need targets
      };

      console.log('[Location] Performing tactical move to:', cell);
      return await this.applyAction(action);
    },
    handleTacticalObjectSelected(obj) {
      console.log('[Location] Tactical object selected:', obj);
      // Update the selected game object when a token is clicked on the tactical map
      if (obj && obj.character_id) {
        this.updateSelectedGameObjectId(obj.character_id);
      } else if (obj && obj.id) {
        this.updateSelectedGameObjectId(obj.id);
      }
    },
    /**
     * Open a chest the player clicked on the tactical map.
     *
     * Two steps, and both are needed. The OPEN_CHEST action is what costs AP and
     * what the GM sees in the log; the modal is what shows the contents. The
     * modal opens even if the action is refused, so the player is told WHY -
     * closing silently on a refusal is indistinguishable from a broken click.
     */
    async handleChestClicked(chest) {
      this.openChestName = chest.name || 'Chest';
      try {
        await this.actionService.performAction({
          actionType: 'OPEN_CHEST',
          targets: [chest.id]
        });
        this.openChestId = chest.id;
        // Opening spends AP, so the HUD is stale the moment it succeeds.
        await this.getPlayerInfo();
        await this.refreshActionLog();
      } catch (error) {
        const detail = error?.response?.data;
        const message = Array.isArray(detail) ? detail[0] : (detail?.detail || detail?.error);
        this.$refs.tacticalMap?.showActionHint?.(
          message || 'You cannot open that chest right now.',
          'warn'
        );
      }
    },

    /**
     * Take a piece of loot off the floor.
     *
     * One action, unlike a chest: there is no container to open first, so the
     * item moves as part of the action that costs the AP and there is no modal
     * to show. The map is re-fetched because the item has left the board and
     * would otherwise go on being drawn on a cell it no longer occupies.
     */
    async handleItemPickedUp(item) {
      try {
        await this.actionService.performAction({
          actionType: 'PICK_UP_ITEM',
          targets: [item.id]
        });
        await this.getPlayerInfo();
        await this.refreshInventory();
        await this.refreshActionLog();
        await this.$refs.tacticalMap?.loadTacticalMap?.();
        this.$refs.tacticalMap?.showActionHint?.(
          `Picked up ${item.name || 'the item'}.`,
          'info'
        );
      } catch (error) {
        const detail = error?.response?.data;
        const message = Array.isArray(detail) ? detail[0] : (detail?.detail || detail?.error);
        this.$refs.tacticalMap?.showActionHint?.(
          message || 'You cannot pick that up right now.',
          'warn'
        );
      }
    },

    /**
     * Walk to a thing, then use it.
     *
     * Two actions, in order, and the second only if the first succeeded — a
     * tactical move is immediate server-side, so by the time it returns the
     * character really is standing next to the target and the reach check the
     * interaction runs will agree. If the move is refused (no AP, blocked
     * route), the interaction is dropped rather than fired from the old cell,
     * where it would be refused in turn and cost the player an explanation
     * about the wrong problem.
     */
    async handleInteractAfterMove({ object, anchor }) {
      const moved = await this.handleTacticalCellClick(anchor);
      if (!moved) return;

      await this.$refs.tacticalMap?.loadTacticalMap?.();

      if (object.type === 'chest') {
        await this.handleChestClicked(object);
      } else {
        await this.handleItemPickedUp(object);
      }
    },

    closeChest() {
      this.openChestId = null;
    },

    /**
     * Refresh after loot changes hands.
     *
     * An emptied chest is deactivated server-side and drops out of
     * `spawned_objects`, so the map has to be re-fetched or the model keeps
     * standing on a cell that no longer has a chest.
     */
    async handleChestLooted({ emptied }) {
      await this.refreshInventory();
      if (emptied) {
        this.closeChest();
        await this.$refs.tacticalMap?.loadTacticalMap?.();
      }
    },

    /**
     * The round's resolution, as the player sees it — and the only way a refresh
     * that can move the log window is allowed to happen.
     *
     * The refresh is the moment a player learns what anyone else did
     * (`action_performed` goes to the master channel, deliberately), and it is also
     * the moment the window can step past a held aim's cycle. A window that moves
     * without the reel running retires that marker having drawn nothing, so the two
     * are one call rather than a pairing each caller has to remember.
     */
    async refreshAndReplay() {
      await this.updateAll();
      this.$refs?.tacticalMap?.playResolutionReel?.(this.actionLog, {trusted: this.actionLogTrusted});
    },

    async updateAll() {
      this.resetAdditionalPlayerData();
      await this.getCurrentPositionInfo();
      this.refreshCharactersFromService();
      await this.getPlayerInfo();
      await this.checkFightStatus(); // Check if player is in a fight
      await this.getPlayerSkills();
      await this.getActiveEffects();
      await this.refreshShields();
      await this.getPlayerSpecials();
      await this.refreshMiniMap();
      await this.refreshActionLog();
      await this.refreshInventory();
      await this.refreshBargains();
      /* After getPlayerInfo, so the character id is available to match against. */
      await this.syncMovementState();
      await this.loadPendingChallenges();
    },
    openInfo() {
      // Show player profile modal instead of opening new window
      this.playerProfileVisible = true;
    },
    closePlayerProfile() {
      this.playerProfileVisible = false;
    },
    async handleGMCharacterSwitch(characterId) {
      console.info(`[Location] GM switching to character ${characterId}`);

      try {
        // Close modal
        this.gmModalOpen = false;

        // Switch character via service
        const result = await GMCharacterSwitchService.switchCharacter(characterId);

        console.info(`[Location] Character switch successful: ${result.character_name}`);

        /*
         * The screen is now looking out of a different body, and the state it was
         * holding described the last one. `tacticalMovementDisabled` is the one that
         * bites: it is a purely local record that *this* character has finished
         * walking this turn, reset only when the cycle or the fight round turns
         * over. Carried across a switch it silently refused every move the new
         * character tried to make, with a page reload the only way out of it. The
         * aimed skill and the picked target are the same mistake in smaller print.
         */
        this.tacticalMovementDisabled = false;
        this.clearSelectedAction();
        this.selectedGameObjectId = null;

        // Reconfigure WebSocket for new character
        await this.reconfigureWebSocketForNewCharacter(characterId);

        // Refresh all game data
        await this.updateAll();

        // Show success message (requires toast plugin or similar)
        if (this.$toast) {
          this.$toast.success(`Now playing as ${result.character_name}`);
        } else {
          console.log(`Now playing as ${result.character_name}`);
        }

      } catch (error) {
        console.error('[Location] Error switching character:', error);
        if (this.$toast) {
          this.$toast.error('Failed to switch character. Please try again.');
        } else {
          alert('Failed to switch character. Please try again.');
        }
      }
    },
    async reconfigureWebSocketForNewCharacter(newCharacterId) {
      console.debug(`[Location] Reconfiguring WebSocket for character ${newCharacterId}`);

      try {
        // Server-side subscriptions are issued at connect time, so the old
        // character's channel stays attached until the socket is re-established.
        this.bus = reconnectConnection();

        /* Every handler, not the four this used to name by hand — the fight
           clock is among the ones that were being dropped. */
        this.subscribeBus();

        // Re-initialize CharacterCurrentLocationService
        await CharacterCurrentLocationService.initialize();

        console.info('[Location] WebSocket reconfigured successfully');

      } catch (error) {
        console.error('[Location] Error reconfiguring WebSocket:', error);
        throw error;
      }
    },
    async diceRoll({dice, index}) {
      try {
        // sleep for 1 second
        await new Promise(r => setTimeout(r, 1000));
        this.diceResult = await this.actionService.diceRoll(dice.value);
        this.refreshActionLog();
      } catch (error) {
        console.error(`Error rolling dice:`, error);
        this.diceResult = null;
      }
    },

    async onDiceRollComplete(result) {
      try {
        // Store the dice roll result
        this.diceResult = result.number;
        console.log('Dice roll complete:', result);

        /* The throw answers the challenge whatever it rolled — the server
           clears it off the character as the action executes — so the
           character is re-read on every outcome. Re-reading only on a success
           left a failed roll holding a challenge the server had already taken
           away, and `closeDice` refuses to close on one: the player was stuck
           behind the roller until the next round.

           The challenge itself is kept until the roller closes, because the
           verdict on screen is read against its difficulty and its modifiers. */
        if (this.currentChallenge) {
          await this.getPlayerInfo();
        }
      } catch (error) {
        console.error('Error handling dice roll result:', error);
        this.diceResult = null;
      }
    },
    async handleEndTurn() {
      try {
        console.log('End turn triggered');
        if (!this.playerInfo || !this.playerGeneralInfo) {
          console.error('Player info not available');
          return;
        }

        // Construct the OpenaiCharacterRequest object
        const request = {
          name: this.playerGeneralInfo.name || '',
          biography: {
            character: this.playerGeneralInfo.biography?.character || ''
          },
          rank: {
            name: this.playerGeneralInfo.rank?.name || ''
          },
          path: {
            name: this.playerGeneralInfo.path?.name || ''
          },
          campaign: {
            id: this.playerGeneralInfo.campaign?.id || ''
          }
        };

        // Call the end turn API
        await CharacterGameApi.characterPlayerEndTurnCreate(request);

        // Update the game state
        await this.updateAll();

        console.log('End turn completed');
      } catch (error) {
        console.error('Error ending turn:', error);
      }
    },
    async handleMove(connection) {
      // Prevent movement if the character is in a fight or has a pending fight
      if (this.isInFight || this.hasPendingFight) {
        console.warn("Cannot move while in a fight or with a pending fight");
        return;
      }

      const direction = connection.direction;
      try {
        const move_direction = this.connections[direction];
        if (!move_direction) {
          console.error(`No connection found for direction: ${direction}`);
          return;
        }
        const new_postion_id = move_direction.to_position;
        await this.actionService.move(new_postion_id);
        /* Asserting travel here outlived the journey: a signed character's move
           resolves the moment it is filed, so the overlay came up over a player
           who had already arrived and nothing was left unperformed to take it
           down again. Ask what is actually in flight instead. */
        await this.syncMovementState();
        if (this.isMoving) {
          await this.getPlayerInfo();
          await this.checkFightStatus();
          return;
        }
        /* Nothing is in flight, so the journey is already over — a signed
           character arrives as the move is filed. Run the same refresh the
           cycle boundary would have run, or the player goes on being shown the
           location they just left until they reload the page. */
        await this.updateAll();
      } catch (error) {
        console.error(`Error handling move to direction ${direction}:`, error);
      }
    },
    async resolveCharacter(characterId) {
      try {
        const data = await CharacterInfoGameService.getCharacterInfo(characterId);
        // const data = (await CharacterGameApi.characterPlayerRetrieve(characterId)).data;
        if (data && data.npc) {
          this.npcCharacters.push(data);
        } else if (data) {
          this.characters.push(data);
        }
      } catch (error) {
        console.error(`Error resolving character ${characterId}:`, error);
      }
    },
    async getCurrentPositionInfo() {
      try {
        // Use CharacterCurrentLocationService to get position data
        this.position = await CharacterCurrentLocationService.getCurrentPositionInfo();

        // Process connections from position data
        this.connections = {}
        if (this.position && this.position.connections) {
          this.position.connections.forEach((conn) => {
            this.connections[conn.direction] = conn;
          });
        }

        // Get characters from CharacterCurrentLocationService
        this.characters = CharacterCurrentLocationService.getPlayerCharacters();
        this.npcCharacters = CharacterCurrentLocationService.getNpcCharacters();
      } catch (error) {
        console.error("Error getting current position info:", error);
        // Initialize with empty values to prevent errors
        this.position = {id: null, image: null, anomalies: [], connections: [], characters: []};
        this.connections = {};
        this.characters = [];
        this.npcCharacters = [];
      }
    },
    async handleActingSignChanged() {
      await this.getPlayerInfo();
    },
    async getPlayerInfo() {
      try {
        this.playerInfo = (await CharacterGameApi.characterPlayerCharacterInfoRetrieve()).data;

        // Always update playerGeneralInfo when playerInfo.id exists
        // This ensures fresh data, especially important during GM character switches
        if (this.playerInfo && this.playerInfo.id) {
          this.playerGeneralInfo = (await CharacterGameApi.characterPlayerRetrieve(this.playerInfo.id)).data;

          // Wait for Vue's reactivity to process the update
          await this.$nextTick();
        }

        this.playerService = new PlayerService(this.playerInfo);

        // Check if character has a current challenge
        if (this.playerGeneralInfo && this.playerGeneralInfo.challenge) {
          console.log("Character has current challenge:", this.playerGeneralInfo.challenge);
          await this.loadCurrentChallenge(this.playerGeneralInfo.challenge);
        }
      } catch (error) {
        console.error("Error getting player info:", error);
        // Initialize with default values to prevent errors
        this.playerInfo = {
          id: null,
          coordinates: null,
          attributes: [] // Add empty attributes array to prevent errors
        };
        if (this.playerGeneralInfo === null) {
          this.playerGeneralInfo = {biography: {avatar: null}};
        }
        this.playerService = new PlayerService(this.playerInfo);
      }
    },
    async loadCurrentChallenge(challengeId) {
      try {
        /* Every refresh of the character re-reads its pending challenge. If it
           is the one already on screen there is nothing to load: re-fetching it
           replaces the challenge the roller is mid-roll on with an identical
           copy, and restarts the modifier cards with it. */
        if (this.diceVisible && this.currentChallenge?.id === challengeId) return;

        console.log("Loading current challenge with ID:", challengeId);

        // Fetch the challenge data using DiceGameApi
        const challengeResponse = await DiceGameApi.diceChallengeRetrieve(challengeId);
        const challenge = challengeResponse.data;

        console.log("Current challenge loaded successfully:", challenge);

        // Store the challenge and open the dice modal
        this.currentChallenge = challenge;
        await this.openDice();

      } catch (error) {
        console.error("Error loading current challenge:", error);
      }
    },
    async getPlayerSkills() {
      try {
        this.playerSkills = (await SkillsGameApi.skillsSkillsList()).data;
      } catch (error) {
        console.error("Error getting player skills:", error);
        this.playerSkills = [];
      }
    },
    async getPlayerSpecials() {
      try {
        this.playerSpecials = (await ActionGameApi.actionSpecialAvailableRetrieve()).data;
      } catch (error) {
        console.error("Error getting player specials:", error);
        this.playerSpecials = [];
      }
    },
    async getActiveEffects() {
      try {
        this.activeEffects = (await EffectsGameApi.effectsActiveList()).data;
      } catch (error) {
        console.error("Error getting active effects:", error);
        this.activeEffects = [];
      }
    },
    // Handle character hover from action log
    handleActionLogCharacterHover({ charId, event }) {
      this.hoverCardCharId = charId;
      this.hoverCardPosition = {
        x: event.clientX + 15,
        y: event.clientY + 15
      };
      this.hoverCardVisible = true;
    },
    // Handle character hover leave from action log
    handleActionLogCharacterLeave() {
      this.hoverCardVisible = false;
      this.hoverCardCharId = null;
    },
    // Handle character click from action log
    handleActionLogCharacterSelected(charId) {
      // Select the character (could open inspector or highlight)
      this.selectedGameObjectId = charId;
      console.log('Character selected from action log:', charId);
    },
  },
  watch: {
    selectedGameObjectId(newId) {
      this.refreshInventory();
    },
    hasPendingFight(newValue) {
      if (newValue) {
        this.updateFightData();
      } else {
        this.resetFightData();
      }
    },
    /* Entering a fight kills every outstanding challenge on the server — a
       sealed character can neither answer one nor be answered. Dropping the
       local copies keeps the overlay from resurfacing when the fight ends. */
    isInFight(newValue) {
      if (newValue) {
        this.pendingChallenges = [];
      }
    },
    /* The page frame lives outside this view, so the accent has to be published
       on the document root for it to follow the acting character. */
    isActingSigned: {
      immediate: true,
      handler(signed) {
        document.documentElement.classList.toggle('game-signed', signed);
      },
    },
  },
};
</script>

<style scoped>
/* Pinned to the viewport's top edge, above the board but below the modals, and
   measured so the board can frame its tokens in what is left. */
.fight-rail-slot {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1500;
}

/* The rail is fixed to the top of the viewport, so the board's own top-edge
   HUDs (grid controls on the left, the movement/vision readout on the right)
   sat underneath it and were unreadable and unclickable. Rather than raise
   them above the rail — which only swaps which one is buried — the rail
   publishes its height and they start below it. Read by PlayerTacticalMap's
   scoped styles through inheritance; 0 when there is no rail. */
.location-view {
  --hud-top-inset: 0rem;
  --game-accent: #ffd700;
  --game-accent-rgb: 255, 215, 0;
}

.location-view--signed {
  --game-accent: #ff00e5;
  --game-accent-rgb: 255, 0, 229;
}

/* Stacked directly above the character-switch button, which is the other
   control that changes who the master is playing. */
.gm-sign-dock {
  position: fixed;
  left: 20px;
  top: calc(80% - 56px);
  transform: translateY(-50%);
  z-index: 9999;
}

.location-view:has(.fight-rail-slot) {
  --hud-top-inset: 2.6rem;
}

.challenge-slot {
  position: fixed;
  bottom: 1.5rem;
  left: 1.5rem;
  z-index: 1500;
}

/* Dead centre of the card band, under the rail. The dial is the one thing on
   screen carrying a deadline, so it takes the middle outright and the cards
   step aside rather than sharing the row and being squeezed by it. */
.fight-dial-slot {
  position: fixed;
  top: calc(1.5rem + var(--hud-top-inset, 0rem));
  left: 50%;
  transform: translateX(-50%);
  z-index: 1400;
  pointer-events: none;
}

/* The cards keep their band but give up the middle of it. */
.top-row--behind-dial {
  padding-top: 6.5rem;
}

/* The establishment stage paints its own scene edge to edge, so the overlay is
   only a positioning frame — no scrim and no backdrop blur to composite over
   the whole viewport behind an already-opaque child. */
.side-donut-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background: #04070a;
}

.rpg-action-holder {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000; /* Above everything for action buttons */
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* Container transparent to clicks */
}

/* Re-enable pointer events for action buttons */
.rpg-action-holder > * {
  pointer-events: auto;
}

/*
 * In transit there is nothing to cast, so the whole bar slides out of the way
 * and leaves only its carved top edge showing — the frame stays, the controls
 * go. It comes back up the moment the character arrives. Nothing is unmounted,
 * so the panel keeps its state and the return needs no re-render.
 */
.rpg-action-holder {
  transition: transform 1.1s cubic-bezier(0.33, 0, 0.2, 1);
}

/* Stops on the frame's yellow rule rather than sliding off the screen edge —
   the bar reads as lowered, not gone. */
.rpg-action-holder--stowed {
  transform: translate(-50%, calc(100% - 2.9rem));
}

.rpg-action-holder--stowed > * {
  pointer-events: none;
}

/* The party cards belong to the place being left, so they lift away with it. */
.top-row {
  transition: transform 1s cubic-bezier(0.33, 0, 0.2, 1), opacity 0.8s ease;
}

.top-row--stowed {
  transform: translateY(-130%);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .rpg-action-holder,
  .top-row {
    transition-duration: 0.2s;
  }
}

.trigger {
  display: flex;
  width: 12rem;
  height: 11rem;
  margin-left: -2.5rem;
  transform: translateY(35%);

}

.center-section {
  display: flex;
  justify-content: space-between;
  gap: 3rem;
  align-items: center;
  height: 60vh;
  max-height: 60vh;
  pointer-events: none; /* Allow clicks to pass through to tactical map */
  z-index: 10; /* Above tactical map but transparent to clicks */
}

/* Re-enable pointer events for interactive children */
.center-section > * {
  pointer-events: auto;
}

.location-view {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  gap: 1rem;
}

.background-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1; /* Base layer, above nothing but below UI */
  display: flex;
}

/* Pinned to the viewport for the same reason the codex rail is. The rail is
   `position: fixed` and so leaves the flex flow entirely, which left
   `space-between` centring the compass inside whatever space remained to the
   right of the bars — never the middle of the screen. */
.center-center {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 15;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  pointer-events: none; /* Allow clicks to pass through when empty */
}

/* Re-enable pointer events for modals and interactive elements */
.center-center > * {
  pointer-events: auto;
}

/* Pinned to the viewport rather than sized by the centre section: the rail is a HUD
   column, and letting flex size it made it resize whenever the middle content changed. */
.center-right {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  display: flex;
  width: clamp(15rem, 21vw, 22rem);
  padding: 4.5vh 0;
  pointer-events: none;
}

.center-right > * {
  pointer-events: auto;
}

/* The rail carries its own scrim so it stays legible over any location art. */
.center-right::before {
  content: '';
  position: absolute;
  /* Reaches past the column so the edge fade happens beside the content, not across it. */
  inset: 0 0 0 -9rem;
  z-index: 0;
  pointer-events: none;
  background:
      radial-gradient(115% 50% at 100% 22%, rgba(20, 78, 96, 0.16), transparent 72%),
      linear-gradient(to left, rgba(3, 11, 16, 0.34) 0%, rgba(3, 11, 16, 0.16) 62%, rgba(3, 11, 16, 0) 100%);
  /* Just enough lift to survive a bright location; over dark art it is barely there. */
  backdrop-filter: blur(7px) saturate(0.8) brightness(0.66);
  -webkit-backdrop-filter: blur(7px) saturate(0.8) brightness(0.66);
  mask-image:
      linear-gradient(to left, #000 34%, transparent 100%),
      linear-gradient(to bottom, transparent 0%, #000 14%, #000 84%, transparent 100%);
  mask-composite: intersect;
}


.codex-rail {
  --codex-spine-w: 16px;
  --codex-spine-gap: 1.1rem;
  --codex-spine: rgba(96, 166, 184, 0.85);
  /* The rail sits straight on the location art with no panel behind it, so every mark
     carries its own dark halo. Invisible over dark scenes, legible over bright ones. */
  --codex-halo: 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85);
  --codex-halo-drop: drop-shadow(0 1px 2px rgba(0, 10, 14, 0.9));
  position: relative;
  z-index: 10;
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  max-height: 100%;
  padding: 0 17% 0 13%;
}

.mini-map {
  flex: 0 0 auto;
  align-self: center;
  width: 100%;
  max-width: 12.4rem;
  margin-bottom: 0.5rem;
}

.codex-note {
  display: flex;
  flex: 0 0 auto;
  gap: var(--codex-spine-gap);
  padding: 0.6rem 0 0.9rem;
}

.codex-note-text {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.3;
  color: #c3cfd6;
  text-shadow: var(--codex-halo, 0 1px 2px rgba(0, 10, 14, 0.95), 0 0 8px rgba(0, 10, 14, 0.85));
}

.action-log {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  z-index: 10;
}


.top-row {
  width: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 5%;
  z-index: 100;
  pointer-events: none; /* Allow clicks to pass through to tactical map */
}

.top-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex: 1;
}

.top-separator {
  width: 1rem;
  height: 100%;
}

.top-left {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
}

.current-turn-component {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 20;
}


.left-action-group {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: row;
  z-index: 20;
  padding-left: 1rem;
}

.right-action-group {
  position: fixed;
  bottom: 2rem;
  right: 0;
  width: 10rem;
  height: 3rem;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0.5rem;
  backdrop-filter: blur(5px);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  flex: 1;
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 1.5rem;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  z-index: 20;
  margin-right: 0.5rem;
  gap: 1rem;
}

.right-action-group * {
  height: 2rem;
  width: 2rem;
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media (max-width: 480px) {
  .compass-component {
    width: 90%;
  }

  .top-row {
    flex-direction: column;
    align-items: center;
  }
}

.center-left {
  margin-left: 0.5rem;
}

/* Visual feedback for no hasTarget selected */
.no-target-feedback {
  animation: shake-highlight 1s ease-in-out;
}

@keyframes shake-highlight {
  0% {
    transform: translateX(0);
    box-shadow: 0 0 0 rgba(255, 87, 87, 0);
  }
  10% {
    transform: translateX(-5px);
  }
  20% {
    transform: translateX(5px);
  }
  30% {
    transform: translateX(-5px);
  }
  40% {
    transform: translateX(5px);
  }
  50% {
    transform: translateX(-3px);
  }
  60% {
    transform: translateX(3px);
  }
  70% {
    transform: translateX(-2px);
  }
  80% {
    transform: translateX(2px);
  }
  90% {
    transform: translateX(-1px);
  }
  100% {
    transform: translateX(0);
    box-shadow: 0 0 0 rgba(255, 87, 87, 0);
  }
}

/* Character Hover Card */
.character-hover-card {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.8));
  animation: fadeIn 0.15s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
